// src/constants/api.ts
var API_DEFAULTS = {
  TIMEOUT_SIMPLE_GET: 5e3,
  TIMEOUT_COMPLEX_GET: 1e4,
  TIMEOUT_MUTATION: 15e3,
  TIMEOUT_FILE_UPLOAD: 36e5,
  DEFAULT_PAGE_SIZE: 25,
  MAX_PAGE_SIZE: 100
};
var DOOR_RELEASE_TABLET_BUDGET_MS = 5e3;
var API_ENDPOINTS = {
  // Auth
  AUTH_LOGIN: "/api/auth/login",
  AUTH_MICROSOFT: "/api/auth/microsoft",
  AUTH_REFRESH: "/api/auth/refresh",
  AUTH_LOGOUT: "/api/auth/logout",
  AUTH_ME: "/api/auth/me",
  AUTH_QR_ACTIVATE: "/api/v1/auth/qr-activate",
  AUTH_DEVICE_TOKEN: "/api/v1/auth/device-token",
  // Zero-touch device auto-provisioning (R1). Public; mints a DeviceToken
  // after an IP + second-factor match. Body: { deviceInfo: { serial|udid|mac } }.
  AUTH_AUTO_PROVISION: "/api/v1/auth/auto-provision",
  AUTH_SIP_CREDENTIALS: "/api/v1/auth/sip-credentials",
  AUTH_SIP_REGISTERED: "/api/v1/auth/sip-registered",
  AUTH_DEVICES_REVOKE_OTHERS: "/api/auth/devices/revoke-others",
  // Device
  DEVICE_REGISTER: "/api/v1/device/register",
  DEVICE_HEARTBEAT: "/api/v1/device/heartbeat",
  DEVICE_CONFIG: "/api/v1/device/config",
  DEVICE_ERROR_LOG: "/api/v1/device/error-log",
  // Lookup a device/caller by SIP extension (R3). Generalized to accept any
  // extension format (no longer restricted to 4 digits) so camera-bearing
  // callers can be resolved before answer.
  DEVICE_BY_EXTENSION: (extension) => `/api/residentApp/devices/by-extension/${extension}`,
  // Self-service Hexnode kiosk toggle for THIS device. Body: { enable: boolean }.
  // Lets staff drop the device out of kiosk to grant OS camera/mic permissions
  // in Android Settings, then re-enable. Device resolved from the device token.
  DEVICE_KIOSK_TOGGLE: "/api/residentApp/device/kiosk",
  // Device reports its current OS permission status (camera/mic/location/etc).
  // Body: { allGranted, missing: string[], permissions: Record<string,string> }.
  // Device resolved from the device token; persisted for the portal overview.
  DEVICE_PERMISSIONS_REPORT: "/api/residentApp/device/permissions",
  // Staff/portal: overview of all resident tablets + their permission status,
  // with site/location for filtering. Query: ?siteId=&locationId=
  TABLET_PERMISSIONS_OVERVIEW: "/api/residentApp/admin/tablet-permissions",
  // Door (R5) — authenticated in-process proxy. DeviceToken identifies the
  // unit server-side; the client sends no unit number or webhook URL.
  OPEN_DOOR: "/api/v1/open-door",
  // Entrance/street door behind the videophone panel — a DIFFERENT door from
  // OPEN_DOOR above, which pulses the resident's OWN UNIT door. Do not reuse
  // one for the other.
  //
  // POST with an EMPTY BODY, and that is the access control: the tablet names
  // no panel, so it cannot open one it is not talking to. The server derives
  // both identities and refuses unless Asterisk's own `call_logs` shows a live
  // call from that panel to this tablet.
  //
  // Answers `{ success, verdict, path, elapsedMs }` where verdict is one of
  // released | already | unsupported | failed | timeout | no_active_call.
  // Spec: specs/20260825-dtmf-to-automationengine-resident-app/spec.md §5.
  ENTRANCE_DOOR_OPEN: "/api/v1/entrance/door-open",
  // User / Resident
  USER_BUTTONS: "/api/v1/user/buttons",
  USER_PHOTOS: "/api/v1/user/photos",
  USER_GEOFENCE_CONFIG: "/api/v1/user/geofence-config",
  // Building
  BUILDING_CONFIG: (id) => `/api/v1/building/${id}/config`,
  BUILDING_CAMERAS: (id) => `/api/v1/building/${id}/cameras`,
  BUILDING_ENTRANCES: (id) => `/api/v1/building/${id}/entrances`,
  // Emergency
  EMERGENCY_TRIGGER: "/api/v1/emergency/trigger",
  EMERGENCY_RESET: "/api/v1/emergency/reset",
  // Alerts
  ALERTS_GEOFENCE: "/api/v1/alerts/geofence",
  // Call tracing — per-system call timeline (see types/telemetry.ts)
  // Device single-event report (DeviceToken auth). Lives under /device/* rather
  // than /calls/* because the API's Asterisk router gates ALL /calls/* paths
  // behind the shared secret. Body carries the CallTraceEvent, so the messy
  // correlation id (SIP Call-ID / linkedid) never has to be URL-encoded.
  // (Asterisk reports its own steps to /api/v1/calls/event with the secret.)
  CALL_EVENT_REPORT: "/api/v1/device/call-event",
  // Portal/staff: fetch the merged event timeline for a call. `id` is the
  // numeric call_logs.id (JWT auth, served by the call-logs admin router).
  CALL_EVENTS: (id) => `/api/call-logs/${id}/events`,
  // Device connectivity heartbeat — buffered 60s samples flushed on change or
  // every ~15 min (DeviceToken auth). Persisted for the portal device view.
  DEVICE_CONNECTIVITY: "/api/v1/device/connectivity",
  // Repair estimates & device catalog (20260702-repair-estimates-catalog)
  // Catalog + tax matrix (authenticated, /api/v1)
  CATALOG_ITEMS: "/api/v1/catalog/items",
  CATALOG_ITEM: (id) => `/api/v1/catalog/items/${id}`,
  CATALOG_ITEM_DEACTIVATE: (id) => `/api/v1/catalog/items/${id}/deactivate`,
  CATALOG_ITEM_STOCK: (id) => `/api/v1/catalog/items/${id}/stock`,
  CATALOG_ITEM_PRICE: (id) => `/api/v1/catalog/items/${id}/price`,
  TAX_RATES: "/api/v1/catalog/tax-rates",
  TAX_RATE: (id) => `/api/v1/catalog/tax-rates/${id}`,
  // Estimates inside tickets (authenticated, /api/v1)
  TICKET_ESTIMATES: (ticketId) => `/api/v1/tickets/${ticketId}/estimates`,
  ESTIMATE: (id) => `/api/v1/estimates/${id}`,
  ESTIMATE_OFFER: (id) => `/api/v1/estimates/${id}/offer`,
  ESTIMATE_CLONE: (id) => `/api/v1/estimates/${id}/clone`,
  // Overview list + invoiced flag (2026-07-17 increment)
  ESTIMATES_LIST: `/api/v1/estimates`,
  ESTIMATE_INVOICED: (id) => `/api/v1/estimates/${id}/invoiced`,
  // The work was effectively carried out (2026-07-30) — the gate ESTIMATE_INVOICED
  // now depends on.
  ESTIMATE_EXECUTED: (id) => `/api/v1/estimates/${id}/executed`,
  // Manual approval by a support employee (2026-07-29). Authenticated + gated on
  // estimates.offer — distinct from ESTIMATE_APPROVE below, which is the PUBLIC,
  // token-bearing surface the client clicks.
  ESTIMATE_MANUAL_APPROVE: (id) => `/api/v1/estimates/${id}/approve`,
  ESTIMATE_MANUAL_DECLINE: (id) => `/api/v1/estimates/${id}/decline`,
  ESTIMATE_PDF: (id) => `/api/v1/estimates/${id}/pdf`,
  // Public approval (bearer = approval token, bare /api prefix — mirrors /api/alarms/uuid/*)
  ESTIMATE_PUBLIC: (uuid) => `/api/estimates/uuid/${uuid}`,
  ESTIMATE_APPROVE: (uuid) => `/api/estimates/uuid/${uuid}/approve`,
  ESTIMATE_DECLINE: (uuid) => `/api/estimates/uuid/${uuid}/decline`,
  ESTIMATE_DELEGATE: (uuid) => `/api/estimates/uuid/${uuid}/delegate`,
  // Flow B — contract-covered finalize (authenticated; mounted on the existing intervention route family)
  INTERVENTION_REPORT_FINALIZE_CONTRACT_COVERED: (interventionId) => `/api/v1/interventions/${interventionId}/finalize-contract-covered`,
  // Portal Knowledge Base (20260706-portal-knowledge-base, LD-ENDPOINTS).
  // Single source of truth for every KB path; the portal derives its calls via
  // stripApiPrefix() and CARO-Api mounts these exact routes. No path is hand-typed.
  // --- Authenticated read (any role) ---
  KB_PAGES: "/api/v1/kb/pages",
  KB_PAGE: (slug) => `/api/v1/kb/pages/${slug}`,
  KB_SEARCH_INDEX: (locale) => `/api/v1/kb/search-index?locale=${locale}`,
  KB_STATUS: "/api/v1/kb/status",
  KB_RUNS: "/api/v1/kb/runs",
  KB_ASSET: (uuid) => `/api/v1/kb/assets/${uuid}`,
  // --- Authenticated write (knowledge_base.manage) ---
  KB_PAGE_CREATE: "/api/v1/kb/pages",
  KB_PAGE_UPDATE: (id) => `/api/v1/kb/pages/${id}`,
  KB_SECTION_OVERRIDE: (id) => `/api/v1/kb/sections/${id}/override`,
  KB_SECTION_REVERT: (id) => `/api/v1/kb/sections/${id}/revert`,
  KB_PAGE_REVERT: (id, rev) => `/api/v1/kb/pages/${id}/revert/${rev}`,
  // Read a page's append-only revision list (admin console history/rollback,
  // T-P2-09). Additive to the §6.5 write family so mock→real stays a pure flag
  // flip and no revision path is hand-typed downstream (LD-ENDPOINTS).
  KB_PAGE_REVISIONS: (id) => `/api/v1/kb/pages/${id}/revisions`,
  KB_ASSET_UPLOAD: "/api/v1/kb/assets",
  // Trigger a manual regen (server dispatches kb-regenerate.yml workflow_dispatch).
  KB_TRIGGER_RUN: "/api/v1/kb/runs",
  // --- Worker → API (CI-credential auth, scoped to /kb/runs/*) ---
  KB_RUN_START: "/api/v1/kb/runs/ingest",
  KB_RUN_PROGRESS: (id) => `/api/v1/kb/runs/${id}`,
  KB_RUN_UPSERT_PAGE: (id) => `/api/v1/kb/runs/${id}/pages`,
  KB_RUN_FINISH: (id) => `/api/v1/kb/runs/${id}/finish`,
  // Site Alarm Board — corridor wall panel (20260804-site-alarm-board).
  // Public, two-factor: every path requires a provisioned DEVICE token; the two
  // that expose alarm detail additionally require the short-lived SESSION token
  // minted by BOARD_UNLOCK. Bare /api prefix, mirroring /api/alarms/uuid/*.
  //
  // Scope note: this group covers the board only. The portal's existing
  // hardcoded alarm literals are deliberately NOT retrofitted here — out of
  // scope for this spec (plan §2.2).
  //
  // Device token → { sessionToken, expiresAt }. Body: { code } — a STRING,
  // never parseInt (the legacy defect at AlarmReset.jsx:86 ate leading zeros).
  BOARD_UNLOCK: (siteUuid) => `/api/board/${siteUuid}/unlock`,
  // Device token alone → { count: N } and NOTHING else, ever (spec §9.1).
  // Feeds the discreet badge on the locked screen. It must never grow a
  // location, type or timestamp — that reintroduces corridor-visible detail.
  BOARD_PENDING_COUNT: (siteUuid) => `/api/board/${siteUuid}/pending-count`,
  // Session token → BoardAlarm[] (see types/alarm.ts) for the site subtree.
  BOARD_ALARMS: (siteUuid) => `/api/board/${siteUuid}/alarms`,
  // Session token. Body: { status: BoardCloseStatus, comment } — 400 below
  // BOARD_COMMENT_MIN_LENGTH.
  BOARD_ALARM_CLOSE: (siteUuid, alarmUuid) => `/api/board/${siteUuid}/alarms/${alarmUuid}/close`,
  // Portal Ideabox (20260817-portal-ideabox §7).
  // Single source of truth for every /api/ideas path; the portal derives its calls via
  // stripApiPrefix() and CARO-Api auto-mounts routes/dynamic/ideas.js at /api/ideas.
  // Modelled on the estimates block above: collections are plain strings, items are
  // typed arrow functions. `ref` is the user-facing public_ref ("#142"), not the PK.
  // --- Board + submit ---
  IDEAS_LIST: "/api/ideas",
  IDEA: (ref) => `/api/ideas/${ref}`,
  // --- Phase 2 (voting). Declared now so no path is ever hand-typed later. ---
  IDEA_VOTE: (ref) => `/api/ideas/${ref}/vote`,
  // --- CARO triage + management (PROTECTED_PERMISSIONS — see permissions.ts:348) ---
  IDEA_TRIAGE: (ref) => `/api/ideas/${ref}/triage`,
  IDEA_QUEUE: (ref) => `/api/ideas/${ref}/queue`,
  IDEA_ROADMAP: (ref) => `/api/ideas/${ref}/roadmap`,
  IDEA_SHIP: (ref) => `/api/ideas/${ref}/ship`,
  IDEA_QUESTION: (ref, oq) => `/api/ideas/${ref}/questions/${oq}`,
  // --- The agent seam. `/agent/*` is declared BEFORE `/:ref` in the router, or a
  // claim request resolves as an idea named "agent". ---
  IDEAS_AGENT_CLAIM: "/api/ideas/agent/claim",
  IDEAS_AGENT_RESULT: "/api/ideas/agent/result",
  IDEAS_AGENT_HEALTH: "/api/ideas/agent/health",
  // Observability feedback loop (20260807-observability-feedback-loop §6).
  //
  // Two audiences behind one prefix, with different authentication. The portal reads through
  // these paths (authMiddleware + monitoring.view); FIX_SHIPPED needs monitoring.manage.
  //
  // Deliberately ABSENT here: /api/observability/runs (POST) and /api/observability/fix-marks.
  // Those are the agent's seam on VM5 — HMAC-signed, no user session — and the agent declares
  // them itself in tools/observability-agent/src/push.js because the signature covers the request
  // line. Listing them here would invite a portal call that can never authenticate.
  //
  // COST is declared before the item routes for the same reason IDEAS_AGENT_* is: the router
  // registers `/cost` ahead of `/:id`, or "cost" resolves as a finding id.
  OBSERVABILITY_COST: "/api/observability/cost",
  OBSERVABILITY_RUNS: "/api/observability/runs",
  OBSERVABILITY_FINDINGS: "/api/observability/findings",
  OBSERVABILITY_FINDING: (id) => `/api/observability/findings/${id}`,
  // The one transition a human owns. Starts a measurement; it does not close anything —
  // the agent decides `resolved` only after a full window that began after this timestamp.
  OBSERVABILITY_FINDING_FIX_SHIPPED: (id) => `/api/observability/findings/${id}/fix-shipped`,
  // ── Paid module entitlements (20260818-invoicingsystem, spec-00) ──────────
  // MODULES_ME needs authentication only, no `modules.view`: every user must be
  // able to learn which surfaces exist. The rest is `modules.manage` (PROTECTED).
  MODULES_ME: "/api/v1/modules/me",
  MODULES_COMPANIES: "/api/v1/modules/companies",
  MODULES_COMPANY: (companyId) => `/api/v1/modules/companies/${companyId}`,
  MODULES_COMPANY_MODULE: (companyId, moduleKey) => `/api/v1/modules/companies/${companyId}/${moduleKey}`,
  // ── Resident invoicing (20260818-invoicingsystem, spec-01) ────────────────
  // Writes are gated requireModule('invoicing'); reads/exports use
  // requireModuleHistory so a departed customer keeps legally-retained records.
  INVOICING_ISSUERS: "/api/v1/invoicing/issuers",
  INVOICING_ISSUER: (companyId) => `/api/v1/invoicing/issuers/${companyId}`,
  INVOICING_ISSUER_SITE: (companyId, siteId) => `/api/v1/invoicing/issuers/${companyId}/sites/${siteId}`,
  INVOICING_ACCOUNTING_CONFIG: "/api/v1/invoicing/accounting-config",
  INVOICING_BILLING_PARTIES: "/api/v1/invoicing/billing-parties",
  INVOICING_BILLING_PARTY: (uuid) => `/api/v1/invoicing/billing-parties/${uuid}`,
  // An APARTMENT's payers. Distinct from INVOICING_BILLING_PARTIES above, which addresses
  // a PERSON's. A flat has two liable parties (owner and resident) and any number of
  // people who only receive a copy — see api/utils/unitPayer.js.
  INVOICING_UNIT_BILLING: (unitLocationId) => `/api/v1/invoicing/units/${unitLocationId}/billing`,
  INVOICING_UNIT_BILLING_PARTIES: (unitLocationId) => `/api/v1/invoicing/units/${unitLocationId}/billing-parties`,
  INVOICING_UNIT_BILLING_PARTY: (uuid) => `/api/v1/invoicing/units/billing-parties/${uuid}`,
  INVOICING_UNIT_RECIPIENTS: (unitLocationId) => `/api/v1/invoicing/units/${unitLocationId}/invoice-recipients`,
  INVOICING_UNIT_RECIPIENT: (uuid) => `/api/v1/invoicing/units/invoice-recipients/${uuid}`,
  INVOICING_ITEMS: "/api/v1/invoicing/billable-items",
  INVOICING_ITEM_EXCLUDE: (uuid) => `/api/v1/invoicing/billable-items/${uuid}/exclude`,
  INVOICING_INVOICES: "/api/v1/invoicing/invoices",
  INVOICING_INVOICE: (uuid) => `/api/v1/invoicing/invoices/${uuid}`,
  INVOICING_INVOICE_ISSUE: (uuid) => `/api/v1/invoicing/invoices/${uuid}/issue`,
  INVOICING_INVOICE_SEND: (uuid) => `/api/v1/invoicing/invoices/${uuid}/send`,
  INVOICING_INVOICE_CREDIT_NOTE: (uuid) => `/api/v1/invoicing/invoices/${uuid}/credit-note`,
  INVOICING_INVOICE_PDF: (uuid) => `/api/v1/invoicing/invoices/${uuid}/pdf`,
  INVOICING_PAYMENTS: "/api/v1/invoicing/payments",
  INVOICING_PAYMENT_ALLOCATIONS: (uuid) => `/api/v1/invoicing/payments/${uuid}/allocations`,
  INVOICING_PAYMENT_ALLOCATION: (uuid, id) => `/api/v1/invoicing/payments/${uuid}/allocations/${id}`,
  INVOICING_EXPORT: "/api/v1/invoicing/export",
  // ── Cafeteria POS (20260818-invoicingsystem, spec-02) ─────────────────────
  // POS_SESSION and POS_BOOTSTRAP need BOTH the terminal token and a staff
  // login; the terminal token identifies the device and never authorises a sale.
  POS_TERMINALS: "/api/v1/pos/terminals",
  POS_TERMINAL_REVOKE: (uuid) => `/api/v1/pos/terminals/${uuid}/revoke`,
  POS_SESSION: "/api/v1/pos/session",
  POS_BOOTSTRAP: "/api/v1/pos/bootstrap",
  POS_SALES: "/api/v1/pos/sales",
  POS_SALE_REVERSE: (uuid) => `/api/v1/pos/sales/${uuid}/reverse`,
  POS_IDENTIFY: "/api/v1/pos/identify",
  // Unattended: room number + PIN. No name list anywhere in the exchange.
  POS_IDENTIFY_ROOM: "/api/v1/pos/identify-room",
  POS_ROOM_PINS: "/api/v1/pos/room-pins",
  // Card payment (Viva). The webhook is mounted OUTSIDE this prefix and outside auth —
  // a payment provider has no portal account. See routes/vivaWebhook.js.
  POS_PAYMENT_CONFIG: "/api/v1/pos/payment-config",
  POS_PAYMENT_CONFIG_TEST: "/api/v1/pos/payment-config/test",
  POS_SALE_CARD: "/api/v1/pos/sales/card",
  INVOICING_INVOICE_PAY_LINK: (uuid) => `/api/v1/invoicing/invoices/${uuid}/pay-link`,
  POS_STAFF: "/api/v1/pos/staff",
  POS_VERIFY_STAFF: "/api/v1/pos/verify-staff",
  POS_SALES_RECENT: "/api/v1/pos/sales/recent",
  POS_CONFIG: (siteId) => `/api/v1/pos/config/${siteId}`,
  POS_CATEGORIES: "/api/v1/pos/categories",
  POS_PRODUCTS: "/api/v1/pos/products",
  POS_RESTRICTIONS: "/api/v1/pos/restrictions",
  // One product, by uuid: DELETE removes it when it was never sold and archives it
  // otherwise (pos_sale_line.product_id is a foreign key and the sales are the record).
  POS_PRODUCT: (uuid) => `/api/v1/pos/products/${uuid}`,
  POS_PRODUCT_STOCK: (uuid) => `/api/v1/pos/products/${uuid}/stock`,
  POS_CATEGORY: (uuid) => `/api/v1/pos/categories/${uuid}`,
  // ⚠ '/site-restrictions', NOT '/restrictions/site'. The latter would be swallowed by
  // '/restrictions/:id', which happily accepts the literal string "site".
  POS_SITE_RESTRICTIONS: "/api/v1/pos/site-restrictions",
  POS_SITE_RESTRICTION: (id) => `/api/v1/pos/site-restrictions/${id}`,
  // The monthly per-apartment statement: one page per apartment plus a total.
  POS_STATEMENT: (siteId) => `/api/v1/pos/statements/${siteId}`,
  POS_STATEMENT_PDF: (siteId) => `/api/v1/pos/statements/${siteId}/pdf`,
  POS_STATEMENT_SEND: (siteId) => `/api/v1/pos/statements/${siteId}/send`,
  /**
   * The statement behind the emailed link. NO session — the HMAC bearer token IS the
   * authorisation, and it rides in the URL fragment so it never reaches an access log.
   * `period` is "YYYY-MM". Always renders WITHOUT resident names.
   */
  POS_STATEMENT_PUBLIC_PDF: (siteId, period) => `/api/v1/pos/statements/public/${siteId}/${period}/pdf`,
  POS_REPORTS: "/api/v1/pos/reports",
  POS_DAY_CLOSES: "/api/v1/pos/day-closes",
  POS_DAY_CLOSE_CLOSE: (siteId, date) => `/api/v1/pos/day-closes/${siteId}/${date}/close`,
  POS_CASH_COUNTS: "/api/v1/pos/cash-counts",
  /**
   * Geld dat in of uit de lade gaat zonder verkoop: afroming naar de kluis, wisselgeld
   * bijgelegd, een uitgave uit de kassa, een correctie.
   *
   * Portaal-only. De kassa-tablet authenticeert als apparaat en heeft geen gebruiker, dus
   * een boeking daar zou geen naam kunnen dragen.
   */
  POS_CASH_MOVEMENTS: "/api/v1/pos/cash-movements",
  POS_CASH_MOVEMENT: (uuid) => `/api/v1/pos/cash-movements/${uuid}`,
  POS_EXPORT: "/api/v1/pos/export",
  // ── Customer onboarding (20260819-registrationandonboarding, spec-01 §8) ──
  //
  // The four PUBLIC entries below are the only unauthenticated writes in this
  // programme. Two rules hold for all of them:
  //   1. They are rate-limited (authLimiter shape). The job is BRUTE-FORCE
  //      resistance on token validation — the form is invite-gated, so this is
  //      not spam control (D10/D11).
  //   2. Their responses never differ based on what exists in the database.
  //      An expired invite and an unknown invite are indistinguishable; a
  //      submission that duplicates an existing customer answers exactly like one
  //      that does not (LD-ONB-NO-DISCLOSURE).
  ONBOARDING_INVITES: "/api/onboarding/invites",
  ONBOARDING_INVITE_DELETE: (id) => `/api/onboarding/invites/${id}`,
  /** PUBLIC. Validates an invite token before rendering the form. */
  ONBOARDING_INVITE_VALIDATE: (token) => `/api/onboarding/invite/${token}`,
  /** PUBLIC. Submits the form. Consumes the invite; creates NO company and NO user. */
  ONBOARDING_APPLICATIONS: "/api/onboarding/applications",
  /** PUBLIC. Keyed by a hashed token — never by the guessable ONB- reference (D19). */
  ONBOARDING_STATUS: (token) => `/api/onboarding/status/${token}`,
  ONBOARDING_APPLICATION: (id) => `/api/onboarding/applications/${id}`,
  /** Gate 2. Creates the company + first company_admin in one transaction. */
  ONBOARDING_APPROVE: (id) => `/api/onboarding/applications/${id}/approve`,
  ONBOARDING_REJECT: (id) => `/api/onboarding/applications/${id}/reject`,
  /** PUBLIC. First password set for the newly created company_admin. */
  ONBOARDING_ACTIVATE: (token) => `/api/onboarding/activate/${token}`,
  // ── Agenda (20260825-animatie-activiteiten-en-agenda §7.1, §7.3) ─────────
  //
  // BASE, not a paid module. `/api/agenda` is the portal surface; the tablet reads
  // through the resident-app namespace below. Neither carries a module gate — the
  // `agenda` BUILDING FEATURE FLAG defaults to TRUE, so gating these would have
  // removed the agenda from every tablet in the field on release day (spec F-2).
  //
  // Resident-PRIVATE entries are stripped SERVER-side (LD-9) for every caller who
  // is neither the owner nor CARO staff. No permission key grants them, and no
  // query parameter here can ask for them.
  AGENDA: "/api/agenda",
  AGENDA_ENTRY: (uuid) => `/api/agenda/${uuid}`,
  /** Tablet: everything reaching one resident, recurrence expanded on read. */
  USER_AGENDA: "/api/v1/user/agenda",
  USER_AGENDA_TODAY: "/api/v1/user/agenda/today",
  /** Tablet: the resident's own items. Default visibility is resident_private. */
  USER_AGENDA_ENTRIES: "/api/v1/user/agenda/entries",
  USER_AGENDA_ENTRY: (uuid) => `/api/v1/user/agenda/entries/${uuid}`,
  // ── Activities (20260825-animatie-activiteiten-en-agenda §7.2, §7.3) ─────
  //
  // The PAID half. Every path below carries requireModule (writes) or
  // requireModuleHistory (reads) AND an `activities.*` permission — two independent
  // gates, neither of which substitutes for the other.
  //
  // ⚠ The literal-prefixed paths are mounted ABOVE `/activities/:uuid` on the server.
  // A single-segment literal after a `/:param` route of the same shape is swallowed by
  // Express, silently, with a 404 that names the wrong thing.
  ACTIVITIES: "/api/activities",
  ACTIVITY: (uuid) => `/api/activities/${uuid}`,
  ACTIVITY_ARCHIVE: (uuid) => `/api/activities/${uuid}/archive`,
  /** Body `{ until, dryRun }`. The dry run writes nothing — it is the safety net for
   *  the deliberate absence of a background materialisation job (LD-4). */
  ACTIVITY_MATERIALISE: (uuid) => `/api/activities/${uuid}/materialise`,
  ACTIVITY_OCCURRENCES: (uuid) => `/api/activities/${uuid}/occurrences`,
  ACTIVITY_OCCURRENCE: (uuid) => `/api/activities/occurrences/${uuid}`,
  ACTIVITY_OCCURRENCE_CANCEL: (uuid) => `/api/activities/occurrences/${uuid}/cancel`,
  /** Per DATE, never per series: a series roster would merge January with June. */
  ACTIVITY_OCCURRENCE_ROSTER: (uuid) => `/api/activities/occurrences/${uuid}/roster`,
  ACTIVITY_OCCURRENCE_SIGNUPS: (uuid) => `/api/activities/occurrences/${uuid}/signups`,
  ACTIVITY_SIGNUP: (uuid) => `/api/activities/signups/${uuid}`,
  ACTIVITY_SITE_CONFIG: (siteId) => `/api/activities/site-config/${siteId}`,
  /** Tablet: detail + signupState + the attendees this resident may select. */
  USER_ACTIVITY_OCCURRENCE: (uuid) => `/api/v1/user/activities/occurrences/${uuid}`,
  USER_ACTIVITY_SIGNUP: (uuid) => `/api/v1/user/activities/occurrences/${uuid}/signup`,
  USER_ACTIVITY_SIGNUP_ROW: (uuid) => `/api/v1/user/activities/signups/${uuid}`,
  // Health
  HEALTH: "/api/health",
  HEALTH_READY: "/api/health/ready"
};

// src/constants/http.ts
var HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
var METHODS_WITH_BODY = ["POST", "PUT", "PATCH"];
var isHttpMethod = (value) => typeof value === "string" && HTTP_METHODS.includes(value);
var methodAllowsBody = (method) => !!method && METHODS_WITH_BODY.includes(method.toUpperCase());
var normalizeWebhookMethod = (config) => {
  const raw = config?.method;
  if (typeof raw === "string") {
    const upper = raw.toUpperCase();
    if (isHttpMethod(upper)) return upper;
  }
  return "POST";
};

// src/constants/sip.ts
var SIP_DEFAULTS = {
  WSS_PORT: 8089,
  // 120s (was 300s): sip.js auto-refreshes at ~90% of expiry, so a device
  // that goes silent is evicted by Asterisk within ~2 min instead of 5,
  // shrinking the window where a call rings into a dead contact.
  REGISTER_EXPIRES: 120,
  KEEPALIVE_INTERVAL: 3e4,
  RECONNECT_MIN: 1e3,
  RECONNECT_MAX: 6e4,
  // Onvoorwaardelijke waakhond. De keepalive-controle helpt hier niet: die keert
  // meteen terug tenzij de state al `registered` is, dus juist in de toestand waar
  // de herverbinding vastligt kijkt niemand. Deze timer draait ongeacht de state.
  WATCHDOG_INTERVAL: 6e4,
  // Hoe lang een niet-geregistreerde client zonder geplande herverbinding mag
  // hangen voordat de waakhond hem forceert. Ruim boven RECONNECT_MAX (60s), zodat
  // een normale backoff nooit onderbroken wordt -- alleen een ketting die echt
  // stilstaat wordt aangeraakt.
  WATCHDOG_STUCK_MS: 15e4
};
var EXTENSION_RANGES = {
  RESIDENT: { min: 1e3, max: 4999 },
  COMMON_AREA: { min: 5e3, max: 5999 },
  STAFF: { min: 6e3, max: 6999 },
  VIDEOPHONE: { min: 7e3, max: 7999 },
  EMERGENCY: { min: 9e3, max: 9002 },
  ENTRANCE_DOOR: { min: 9100, max: 9199 }
};
var isEmergencyExtension = (ext) => {
  const num = typeof ext === "string" ? parseInt(ext, 10) : ext;
  return num >= EXTENSION_RANGES.EMERGENCY.min && num <= EXTENSION_RANGES.EMERGENCY.max;
};
var isVideophoneExtension = (ext) => {
  const num = typeof ext === "string" ? parseInt(ext, 10) : ext;
  return num >= EXTENSION_RANGES.VIDEOPHONE.min && num <= EXTENSION_RANGES.VIDEOPHONE.max;
};
var isEntranceDoorExtension = (ext) => {
  const num = typeof ext === "string" ? parseInt(ext, 10) : ext;
  return num >= EXTENSION_RANGES.ENTRANCE_DOOR.min && num <= EXTENSION_RANGES.ENTRANCE_DOOR.max;
};

// src/constants/errors.ts
var ERROR_CODES = {
  // Auth
  AUTH_EXPIRED: "AUTH_EXPIRED",
  AUTH_INVALID: "AUTH_INVALID",
  AUTH_REFRESH_FAILED: "AUTH_REFRESH_FAILED",
  AUTH_DEVICE_NOT_REGISTERED: "AUTH_DEVICE_NOT_REGISTERED",
  AUTH_QR_INVALID: "AUTH_QR_INVALID",
  AUTH_QR_EXPIRED: "AUTH_QR_EXPIRED",
  // Network
  NETWORK_OFFLINE: "NETWORK_OFFLINE",
  NETWORK_TIMEOUT: "NETWORK_TIMEOUT",
  API_UNREACHABLE: "API_UNREACHABLE",
  SIP_UNREACHABLE: "SIP_UNREACHABLE",
  // Call
  CALL_FAILED: "CALL_FAILED",
  CALL_REJECTED: "CALL_REJECTED",
  CALL_NO_MEDIA: "CALL_NO_MEDIA",
  CALL_ICE_FAILED: "CALL_ICE_FAILED",
  // Emergency
  EMERGENCY_CALL_FAILED: "EMERGENCY_CALL_FAILED",
  EMERGENCY_SIP_UNAVAILABLE: "EMERGENCY_SIP_UNAVAILABLE",
  // General
  PERMISSION_DENIED: "PERMISSION_DENIED",
  NOT_FOUND: "NOT_FOUND",
  SERVER_ERROR: "SERVER_ERROR",
  VALIDATION_ERROR: "VALIDATION_ERROR"
};
var SUPPORT_CONTACT = {
  email: "support@caro.care",
  phone: "09/543.21.21"
};

// src/constants/domain.ts
var APP_NAME = "CARO";
var APP_VERSION = "2.0.0";
var AUTH_KEYS = {
  TOKEN: "caro_auth_token",
  REFRESH_TOKEN: "caro_refresh_token",
  USER_DATA: "caro_user_data"
};
var PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100]
};
var DATE_FORMATS = {
  DATE: "yyyy-MM-dd",
  TIME: "HH:mm:ss",
  DATETIME: "yyyy-MM-dd HH:mm:ss",
  DATE_DISPLAY: "MMM dd, yyyy",
  DATETIME_DISPLAY: "MMM dd, yyyy HH:mm"
};
var LOCATION_TYPES = [
  { id: 1, friendlyName: "Site", value: "site" },
  { id: 2, friendlyName: "Building", value: "building" },
  { id: 3, friendlyName: "Floor", value: "floor" },
  { id: 4, friendlyName: "Unit", value: "unit" },
  { id: 5, friendlyName: "Room", value: "room" },
  { id: 6, friendlyName: "Hallway", value: "hallway" },
  { id: 7, friendlyName: "Restroom", value: "restroom" },
  { id: 8, friendlyName: "CARO_Stock", value: "stock" },
  { id: 9, friendlyName: "Parking", value: "parking" },
  { id: 10, friendlyName: "Common Area", value: "commonarea" },
  { id: 11, friendlyName: "Basement", value: "basement" },
  { id: 12, friendlyName: "Garden", value: "garden" },
  { id: 13, friendlyName: "Fitness", value: "fitness" },
  { id: 14, friendlyName: "Hairdresser", value: "hairdresser" },
  { id: 15, friendlyName: "Wellness", value: "wellness" },
  { id: 16, friendlyName: "Physical Medicine", value: "physicalmedicine" },
  { id: 17, friendlyName: "Restaurant", value: "restaurant" },
  { id: 18, friendlyName: "Bicycle Storage", value: "bicyclestorage" }
];
var DEVICE_TYPES = {
  ANDROID_TABLET: 9,
  UNIFI_GATEWAY: 18,
  UNIFI_SWITCH: 19,
  UNIFI_ACCESS_POINT_IW: 20,
  UNIFI_ACCESS_POINT: 21
};
var DEVICE_STATUS = {
  ONLINE: "online",
  OFFLINE: "offline",
  MAINTENANCE: "maintenance",
  ERROR: "error"
};
var USER_ROLES = {
  ADMIN: "admin",
  COMPANY_ADMIN: "company_admin",
  COMPANY_MANAGER: "company_manager",
  SITE_MANAGER: "site_manager",
  USER: "user"
};
var PORTAL_ROLES = [
  USER_ROLES.ADMIN,
  USER_ROLES.COMPANY_ADMIN,
  USER_ROLES.COMPANY_MANAGER,
  USER_ROLES.SITE_MANAGER,
  USER_ROLES.USER
];
var USER_ROLE_LABELS = {
  [USER_ROLES.ADMIN]: "Admin",
  [USER_ROLES.COMPANY_ADMIN]: "Company Admin",
  [USER_ROLES.COMPANY_MANAGER]: "Company Manager",
  [USER_ROLES.SITE_MANAGER]: "Site Manager",
  [USER_ROLES.USER]: "User"
};
var USER_ROLE_DESCRIPTIONS = {
  [USER_ROLES.ADMIN]: "Full platform access across all companies",
  [USER_ROLES.COMPANY_ADMIN]: "Full access within their own company",
  [USER_ROLES.COMPANY_MANAGER]: "Manages users, rights and sites within their company",
  [USER_ROLES.SITE_MANAGER]: "Manages assigned sites, can approve camera requests and assign users",
  [USER_ROLES.USER]: "Basic access \u2014 view devices, locations and tickets"
};
var SITE_CUSTOM_FIELDS = {
  SITE_MANAGER_EMAIL: "site_manager_email",
  SITE_MANAGER_USER_ID: "site_manager_user_id",
  // Repair-estimates / invoicing custom fields (20260702-repair-estimates-catalog).
  // Read via `@caro/shared/utils/invoicing` helpers, mirroring getSiteManagerEmail.
  INVOICING_EMAIL: "invoicing_mail",
  // invoicing party's email (LD-INVOICE-MAIL)
  CLIENT_TAX_PROFILE: "tax_profile",
  // client tax-profile code (LD-TAX, CR-8)
  SERVICE_CONTRACT: "service_contract",
  // boolean flag: site under a service contract; gates Flow B (CR-12)
  // Per-site approval window in DAYS (2026-07-29). The 72h default is too short
  // for a client whose approval needs a board decision (a VME/syndicus meets
  // monthly); unset falls back to ESTIMATE_APPROVAL_DEFAULT_DAYS.
  ESTIMATE_APPROVAL_DAYS: "estimate_approval_days"
};
var INVOICING_SUBDIVISIONS = {
  RENT: "RENT",
  CARE: "CARE",
  CAFETARIA: "CAFETARIA",
  BRASSERIE: "BRASSERIE",
  ACTIVITY: "ACTIVITY",
  PHARMACY: "PHARMACY",
  SHOP: "SHOP",
  MISC: "MISC"
};
var BILLABLE_SOURCE_TYPES = {
  POS: "pos",
  RENT: "rent",
  CARE: "care",
  EVENT: "event",
  PHARMACY: "pharmacy",
  SHOP: "shop",
  MANUAL: "manual"
};
var POS_TAX_CATEGORIES = {
  FOOD_ONSITE: "food_onsite",
  FOOD_TAKEAWAY: "food_takeaway",
  DRINK_ONSITE: "drink_onsite",
  DRINK_TAKEAWAY: "drink_takeaway",
  ALCOHOL: "alcohol",
  RENT: "rent",
  CARE: "care",
  ACTIVITY: "activity",
  MISC: "misc"
};
var RESIDENT_TAX_PROFILE = "resident_be";
var POS_CONSUMPTION_MODES = {
  ONSITE: "onsite",
  TAKEAWAY: "takeaway"
};
var POS_SETTLEMENTS = {
  CASH: "cash",
  CARD: "card",
  ON_ACCOUNT: "on_account"
};
var POS_IDENTIFICATION_MODES = {
  PICK_NAME: "pick_name",
  PICK_NAME_PIN: "pick_name_pin",
  NFC: "nfc"
};
var ESTIMATE_APPROVAL_DEFAULT_DAYS = 3;
var ESTIMATE_APPROVAL_MIN_DAYS = 1;
var ESTIMATE_APPROVAL_MAX_DAYS = 90;
var CAMERA_REQUEST_STATUS = {
  submitted: {
    i18nKey: "CameraRequests.status_submitted",
    label: "Submitted",
    color: "default"
  },
  pending_site_approval: {
    i18nKey: "CameraRequests.status_pending_site_approval",
    label: "Pending Site Manager",
    color: "warning"
  },
  pending_dpo_approval: {
    i18nKey: "CameraRequests.status_pending_dpo_approval",
    label: "Pending DPO",
    color: "info"
  },
  approved_pending_retrieval: {
    i18nKey: "CameraRequests.status_approved_pending_retrieval",
    label: "Approved - Pending Footage",
    color: "primary"
  },
  completed: {
    i18nKey: "CameraRequests.status_completed",
    label: "Completed",
    color: "success"
  },
  // Real backend terminal statuses (camera_footage_requests ENUM).
  rejected: {
    i18nKey: "CameraRequests.status_rejected",
    label: "Rejected",
    color: "error"
  },
  closed_no_footage: {
    i18nKey: "CameraRequests.status_closed_no_footage",
    label: "No Footage Found",
    color: "default"
  },
  // Granular aliases (tolerated if a future migration splits the terminal states).
  rejected_by_site_manager: {
    i18nKey: "CameraRequests.status_rejected_by_site_manager",
    label: "Rejected by Site Manager",
    color: "error"
  },
  rejected_by_dpo: {
    i18nKey: "CameraRequests.status_rejected_by_dpo",
    label: "Rejected by DPO",
    color: "error"
  },
  closed_footage_not_found: {
    i18nKey: "CameraRequests.status_closed_footage_not_found",
    label: "No Footage Found",
    color: "default"
  }
};
function getCameraRequestStatusMeta(status) {
  return CAMERA_REQUEST_STATUS[status] || {
    i18nKey: "",
    label: (status || "").replace(/_/g, " "),
    color: "default"
  };
}
var ESTIMATE_STATUS = {
  draft: {
    i18nKey: "Estimates.status_draft",
    label: "Draft",
    color: "default"
  },
  offered: {
    i18nKey: "Estimates.status_offered",
    label: "Offered",
    color: "info"
  },
  approved: {
    i18nKey: "Estimates.status_approved",
    label: "Approved",
    color: "success"
  },
  declined: {
    i18nKey: "Estimates.status_declined",
    label: "Declined",
    color: "error"
  },
  expired: {
    i18nKey: "Estimates.status_expired",
    label: "Expired",
    color: "default"
  },
  contract_covered: {
    i18nKey: "Estimates.status_contract_covered",
    label: "Contract Covered",
    color: "success"
  },
  // Derived display status (executed_at set, not yet invoiced) — 2026-07-30.
  // The work is done and therefore billable; approval alone never meant that.
  to_invoice: {
    i18nKey: "Estimates.status_to_invoice",
    label: "To invoice",
    color: "warning"
  },
  // Derived display status (invoiced_at set) — 2026-07-17 increment.
  invoiced: {
    i18nKey: "Estimates.status_invoiced",
    label: "Invoiced",
    color: "primary"
  }
};
function getEstimateStatusMeta(status) {
  return ESTIMATE_STATUS[status] || {
    i18nKey: "",
    label: (status || "").replace(/_/g, " "),
    color: "default"
  };
}
var IN_STOCK_STATUS = {
  IN_STOCK: {
    i18nKey: "Catalog.in_stock",
    label: "In stock",
    color: "success"
  },
  OUT_OF_STOCK: {
    i18nKey: "Catalog.out_of_stock",
    label: "Out of stock",
    color: "default"
  }
};
function getInStockMeta(inStock) {
  return Number(inStock) > 0 ? IN_STOCK_STATUS.IN_STOCK : IN_STOCK_STATUS.OUT_OF_STOCK;
}
var NOTIFICATION_TYPES = {
  INFO: "info",
  SUCCESS: "success",
  WARNING: "warning",
  ERROR: "error"
};
var WS_CONFIG = {
  RECONNECT_INTERVAL: 5e3,
  MAX_RECONNECT_ATTEMPTS: 10
};
var FILE_UPLOAD = {
  MAX_FILE_SIZE: 20 * 1024 * 1024 * 1024,
  // 20GB
  ALLOWED_FILE_TYPES: [
    "image/jpeg",
    "image/png",
    "image/gif",
    "application/pdf",
    "text/csv",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ]
};
var CACHE_CONFIG = {
  TTL: 5 * 60 * 1e3,
  // 5 minutes
  KEY_PREFIX: "caro_cache_"
};
var ERROR_MESSAGES = {
  NETWORK_ERROR: "Network error. Please check your connection.",
  UNAUTHORIZED: "You are not authorized to perform this action.",
  SESSION_EXPIRED: "Your session has expired. Please login again.",
  GENERIC_ERROR: "An unexpected error occurred. Please try again.",
  VALIDATION_ERROR: "Please check your input and try again."
};
var SUCCESS_MESSAGES = {
  SAVED: "Changes saved successfully.",
  DELETED: "Item deleted successfully.",
  UPDATED: "Item updated successfully.",
  CREATED: "Item created successfully."
};
var REGEX = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^[\d\s\-+()]+$/,
  URL: /^https?:\/\/.+/,
  ALPHANUMERIC: /^[a-zA-Z0-9]+$/
};
var THEME_COLORS = {
  PRIMARY: "#1976d2",
  SECONDARY: "#dc004e",
  SUCCESS: "#4caf50",
  WARNING: "#ff9800",
  ERROR: "#f44336",
  INFO: "#2196f3"
};
var MAP_DEFAULTS = {
  CENTER: [51.505, -0.09],
  ZOOM: 13
};
var POS_PRODUCT_ICONS = [
  { key: "soda", label: "Frisdrank", group: "Dranken" },
  { key: "water", label: "Fles water", group: "Dranken" },
  { key: "coffee", label: "Koffie", group: "Dranken" },
  { key: "tea", label: "Thee", group: "Dranken" },
  { key: "milk", label: "Melk / Cecemel", group: "Dranken" },
  { key: "beer", label: "Bierglas", group: "Alcohol" },
  { key: "wine_red", label: "Glas rode wijn", group: "Alcohol" },
  { key: "wine_white", label: "Glas witte wijn", group: "Alcohol" },
  { key: "wine_rose", label: "Glas rose", group: "Alcohol" },
  { key: "cava", label: "Glas cava", group: "Alcohol" },
  { key: "bottle_wine", label: "Fles wijn / cava", group: "Alcohol" },
  { key: "cocktail", label: "Cocktail", group: "Alcohol" },
  { key: "ice_cone", label: "IJshoorn", group: "Eten" },
  { key: "ice_stick", label: "IJsje aan stok", group: "Eten" },
  { key: "ice_cup", label: "Potje ijs", group: "Eten" },
  { key: "crisps", label: "Chips", group: "Eten" },
  { key: "sandwich", label: "Broodje", group: "Eten" },
  { key: "pastry", label: "Gebak", group: "Eten" },
  { key: "snack", label: "Snack", group: "Overig" },
  { key: "gift", label: "Cadeau / actie", group: "Overig" }
];
var POS_STOCK_MODES = ["ignore", "warn", "block"];
var POS_TILE_SIZES = ["small", "medium", "large"];

// src/constants/permissions.ts
var PERMISSION_GROUPS = {
  auth: ["login"],
  dashboard: ["dashboard.view"],
  alarms: ["alarms.view", "alarms.create", "alarms.edit", "alarms.delete"],
  alarm_flows: ["alarm_flows.view", "alarm_flows.edit", "alarm_flows.manage"],
  automations: ["automations.view", "automations.edit", "automations.manage"],
  qbus: ["qbus.view", "qbus.edit", "qbus.manage"],
  telephony: ["telephony.view", "telephony.edit", "telephony.manage"],
  firmware: ["firmware.view", "firmware.create", "firmware.edit", "firmware.delete"],
  services: ["services.view", "services.manage"],
  device_types: ["device_types.view", "device_types.manage"],
  device_linking: ["device_linking.manage"],
  baseip: ["baseip.manage"],
  enocean: ["enocean.view", "enocean.edit", "enocean.import"],
  eink: ["eink.manage"],
  indoor: ["indoor.view", "indoor.manage"],
  monitoring: ["monitoring.view", "monitoring.manage"],
  unit_audit: ["unit_audit.view", "unit_audit.manage"],
  notifications: ["notifications.view", "notifications.manage"],
  settings: ["settings.view"],
  system: ["system.admin", "system.logs"],
  integrations: ["integrations.manage"],
  emergency_services: ["emergency_services.manage"],
  devices: [
    "devices.view",
    "devices.create",
    "devices.edit",
    "devices.delete",
    "devices.manage",
    "devices.admin",
    "devices.bulk-action",
    // Physically unlocking a door on a 2N intercom (POST /api/devices/:id/twon/door-unlock).
    //
    // The portal has gated its door button on this key since the 2N integration
    // shipped — but the key was never in this catalog, so `hasPermission` resolved it
    // to false for every ordinary role and true for FULL_ACCESS_ROLES. The check
    // looked right while consulting nothing, and the API route carried no gate at all,
    // so ANY authenticated user of ANY tenant could open ANY door by device id.
    //
    // It is a separate key from devices.edit on purpose: opening a door in a care
    // facility is a physical-security act, not device administration. Deliberately
    // granted to NO role by default — it has to be handed out on purpose.
    "devices.control_2n",
    // Rebooting a 2N intercom and placing a test call. Both were checked by the
    // portal (TwoNDeviceActions.jsx:52-53) against keys that did not exist, so they
    // resolved to false for every ordinary role and true for FULL_ACCESS_ROLES.
    // Separate from devices.edit because they ACT on a live device in a resident's
    // home rather than change a record. Granted to no role by default.
    "devices.restart",
    "devices.test_call"
  ],
  locations: [
    "locations.view",
    "locations.create",
    "locations.edit",
    "locations.delete",
    "locations.manage",
    // PROTECTED. Repairing a location that has no company_id is the act of ASSIGNING
    // A TENANT, and the screen that does it necessarily lists orphan locations across
    // EVERY customer. `system.admin` would have been the lazy choice and is wrong:
    // it is not protected, so company_admin inherits it through the FULL_ACCESS_ROLES
    // short-circuit and a customer's own admin would see other customers' sites.
    "locations.tenant_repair",
    // 2026-09-02. Verify/correct `locations.data.phoneNumber` — the number every alarm
    // flow dials via {{loc.data.phoneNumber}} and the videophone test hands to a 2N.
    // Tenant-scoped through locations.company_id, so NOT protected; but granted to
    // no role by default, because it lists and rewrites what every flow dials.
    "locations.phone_numbers"
  ],
  tickets: [
    "tickets.view",
    "tickets.create",
    "tickets.edit",
    "tickets.delete",
    "tickets.manage",
    "tickets.assign",
    "tickets.close"
  ],
  camera_requests: [
    "camera_requests.view",
    "camera_requests.create",
    "camera_requests.approve_site",
    "camera_requests.approve_dpo",
    "camera_requests.retrieve_footage"
  ],
  // 20260831-cameras-als-toestel (OQ-4). Bewust NIET camera_requests.* uitbreiden.
  //
  // `camera_requests.*` hoort bij de tweetraps goedkeuringsstroom voor OPGENOMEN beeld
  // (SiteManager → DPO, met legal_basis en retention_period_days). Dit is live beeld
  // tijdens een aanbeloproep — een ander verwerkingsdoel, met een andere afweging en een
  // andere bewaartermijn (namelijk: geen). Twee onvergelijkbare verwerkingsdoelen op één
  // sleutel is precies hoe een GDPR-gate zijn betekenis verliest: wie de ene intrekt,
  // trekt ongemerkt de andere in, en wie de ene verleent, verleent er twee.
  cameras: [
    "cameras.view",
    "cameras.edit",
    "cameras.manage",
    // Bepaalt WIE MEEKIJKT. Stond kort in PROTECTED_PERMISSIONS; op 2026-09-03 door de
    // eigenaar teruggedraaid — `admin` is het hoogste niveau en hoort alles te hebben.
    // Zie de noot bij PROTECTED_PERMISSIONS voor wat dat voor `company_admin` betekent.
    "cameras.manage_links"
  ],
  field_interventions: [
    "field_interventions.view",
    "field_interventions.view_all",
    "field_interventions.create",
    "field_interventions.edit",
    "field_interventions.delete"
  ],
  // Repair estimates & device catalog (20260702-repair-estimates-catalog).
  // Mirrored in CARO-Api/api/middleware/permissionsCatalog.js (permissions.contract
  // test fails CI on drift — [API] task T009).
  // `catalog.view` is the Shop door: it lists items, SKUs, stock and tax category
  // but NOT prices. `catalog.view_prices` is a deliberately separate right so an
  // account can browse the catalog without seeing what anything costs (decision
  // 2026-07-17). Prices are stripped SERVER-SIDE in catalogController.serializeItem —
  // the React gate is a UI affordance, not the boundary. `catalog.manage` implies
  // price visibility (you cannot append a price you cannot read).
  catalog: ["catalog.view", "catalog.view_prices", "catalog.manage"],
  estimates: [
    "estimates.view",
    "estimates.create",
    "estimates.offer",
    "estimates.bill_without_approval"
  ],
  // Portal Knowledge Base (20260706-portal-knowledge-base, LD-PERMS).
  // `view` is reserved for the v2 role-scoping path (granted to all roles in v1);
  // `manage` gates authoring/override/revert and triggering a manual regen.
  // Mirrored in CARO-Api/api/middleware/permissionsCatalog.js (permissions.contract
  // test fails CI on drift).
  knowledge_base: ["knowledge_base.view", "knowledge_base.manage"],
  // Site Alarm Board — corridor wall panels (20260804-site-alarm-board §4.8).
  // `manage` provisions panels, rotates the per-site unlock code and clears a
  // lockout. It is deliberately a SINGLE key with no separate `view`: every
  // read on this surface (the panel list, lastSeenAt, the lockout state) is
  // operational security information, and there is no useful read-only role
  // for it. Note the code itself is never readable by anyone — it is bcrypt
  // -hashed, so `manage` grants rotation, not disclosure.
  // Mirrored in CARO-Api/api/middleware/permissionsCatalog.js (permissions.contract
  // test fails CI on drift).
  site_board: ["site_board.manage"],
  // PII — separate group per locked decision (Q-B 2026-04-28).
  persons: [
    "persons.view",
    "persons.create",
    "persons.edit",
    "persons.delete",
    "persons.invite"
  ],
  persontypes: ["persontypes.manage"],
  users: [
    "users.view",
    "users.edit",
    "users.manage",
    "users.permissions.manage"
  ],
  company: [
    "company.view",
    "company.edit",
    "company.manage",
    "company.settings.manage"
  ],
  // `files.view` en `files.download` zijn op 2026-09-03 GEPENSIONEERD (specs/20260902-
  // documentclassificatie, D-12). Ze bewaakten niets — /api/files is 9/9 ongegate — en
  // drie naamruimten voor één recht is hoe de volgende lezer concludeert dat er
  // toegangscontrole bestaat. Lezen is vanaf nu het antwoord van de engine
  // (`documentAccessEngine`), nooit een sleutel. Migratie 20260910100600 haalt beide uit
  // elke `users.permissions`-envelop en elke template.
  files: [
    "files.upload",
    "files.delete",
    "files.share",
    "files.manage"
  ],
  // ── Documentclassificatie (specs/20260902-documentclassificatie/spec.md §6) ──
  //
  // Twee assen. De zes `documents.read.<topic>`-sleutels zijn de UI-vorm van een topic-grant:
  // een template die er één bevat schrijft bij het stempelen een rij in `document_topic_grants`
  // (LD-5), en DIE tabel is de enige bron die de engine leest — nooit de envelop. Een bestand
  // zonder topic vereist geen enkele sleutel (D-14), daarom is er geen `documents.read.general`.
  //
  // `documents.classify` is het enige gegate LABEL-recht: verlagen, topics wijzigen, naar een
  // losser pad verplaatsen (LD-8). Verhogen is vrij.
  //
  // ⚠ De drie admin-sleutels zijn PROTECTED (zie onder): `company_admin` zit in
  // FULL_ACCESS_ROLES en zou anders bij elke klant op dag één zichzelf een `strikt`-grant
  // kunnen geven, een collega kunnen uitsluiten, of een batch kunnen verwijderen.
  // `documents.access_admin` en `documents.exclusion_admin` mogen NIET samen op één account
  // (P6/E1, PERMISSION_INCOMPATIBLE_PAIRS hieronder).
  //
  // ⚠ GEEN van deze tien gaat in ROLE_DEFAULT_PERMISSIONS of in een permissietemplate.
  // Spec §11 stap 4 seedt twee globale templates via een aparte, geauditeerde migratie —
  // zichtbaar, niet via een role default die zich bij de volgende apply naar elke klant
  // verspreidt.
  documents: [
    "documents.read.medical",
    "documents.read.financial",
    "documents.read.legal",
    "documents.read.technical",
    "documents.read.housing",
    "documents.read.hr",
    "documents.classify",
    "documents.access_admin",
    "documents.exclusion_admin",
    "documents.retention"
  ],
  reports: ["reports.view", "reports.export"],
  // Portal Ideabox (spec 20260817-portal-ideabox §8, §11.1).
  //
  // ⚠ `.triage` and `.manage` are PROTECTED_PERMISSIONS (see below). That is not a
  // formality: FULL_ACCESS_ROLES includes `company_admin`, which is a PER-CUSTOMER
  // role, so adding these the ordinary way would hand every customer's own admin the
  // right to publish, redact, decline and merge on day one — a GDPR-adjacent
  // disclosure permission granted by omission. Triage is a data-protection decision
  // in front of an AI processor and an email fan-out, not a quality decision.
  //
  // `.vote` is a separate key from `.view` purely so abuse can be revoked without
  // blinding someone.
  ideas: ["ideas.view", "ideas.create", "ideas.vote", "ideas.triage", "ideas.manage"],
  // ── Paid modules (20260818-invoicingsystem, spec-00) ─────────────────────
  // `.view` tells a user which modules their own organisation has. `.manage` is
  // PROTECTED_PERMISSIONS: enabling a BIJBETALENDE module is a commercial act, and
  // FULL_ACCESS_ROLES includes `company_admin` — a per-customer role that must not
  // be able to grant itself a paid module (D25 / LD-MOD-CARO-ONLY).
  modules: ["modules.view", "modules.manage"],
  // ── Audit trail / NIS2 reporting (2026-08-25) ────────────────────────
  // Reading the audit trail is itself a processing of personal data: it shows which
  // named member of staff opened which resident's record, and when. So both keys are
  // PROTECTED — FULL_ACCESS_ROLES includes `company_admin`, a per-customer role, and
  // handing every customer's own administrator a surveillance view of their staff by
  // omission is precisely the kind of grant that has to be a decision.
  //
  // `.export` is separate from `.view` because a CSV leaves the building: it can be
  // mailed, kept after someone leaves, and is no longer subject to our retention.
  audit: ["audit.view", "audit.export"],
  // ── Resident invoicing (20260818-invoicingsystem, spec-01) ────────────────
  // `.view_amounts` is PROTECTED for the same structural reason as
  // camera_requests.approve_dpo: resident financial data must be an explicit
  // grant, not something a customer admin inherits on day one (D22).
  //
  // ⚠ ONBOARDING CONSEQUENCE, stated so it is not discovered as a support ticket:
  // a new customer's own admin sees the invoicing module with EVERY AMOUNT BLANK
  // until CARO grants `invoicing.view_amounts`. That is deliberate. It belongs in
  // the go-live runbook (plan.md §11.3 item 1).
  invoicing: [
    "invoicing.view",
    "invoicing.view_amounts",
    "invoicing.manage",
    "invoicing.issue",
    "invoicing.payments",
    "invoicing.config",
    "invoicing.export"
  ],
  // ── Cafeteria POS (20260818-invoicingsystem, spec-02) ─────────────────────
  // `.report_persons` is PROTECTED: a named resident's itemised consumption —
  // including alcohol — is the most sensitive payload in this system (D22).
  pos: [
    "pos.view",
    "pos.sell",
    "pos.catalog",
    "pos.restrictions",
    "pos.override_restriction",
    "pos.reports",
    "pos.report_persons",
    "pos.cash_count",
    "pos.terminals",
    "pos.export"
  ],
  // ── Agenda (20260825-animatie-activiteiten-en-agenda, LD-1) ──────────────
  //
  // BASE, not a paid module. The agenda itself is the free layer: three tiers
  // (company / site / unit) of plain calendar entries, no signups and no money.
  // The `agenda` BUILDING FEATURE FLAG (features/featureDefaults.ts) defaults to
  // TRUE and is a different question entirely — it decides whether a TABLET shows
  // an agenda, and is deliberately left alone (spec F-2).
  //
  // These two keys also close a live cross-tenant hole: until 2026-08-25 the staff
  // agenda router carried `authMiddleware` and nothing else, so any authenticated
  // portal user of any customer read and wrote any other customer's agenda by
  // changing one integer in the query string. Phase 0 of that spec puts these keys
  // in front of it.
  //
  // ⚠ Resident-PRIVATE entries hang off NEITHER key (LD-9). FULL_ACCESS_ROLES
  // short-circuits every checkPermission, so a key would protect nothing against a
  // customer's own admin. Private entries are stripped SERVER-side for everyone who
  // is neither the owner nor in CROSS_ORG_ROLES (`admin` / `super_admin`).
  agenda: ["agenda.view", "agenda.manage"],
  // ── Activities (20260825-animatie-activiteiten-en-agenda, §6) ────────────
  //
  // The PAID half of the same feature. Module `activities` and these keys are TWO
  // INDEPENDENT GATES: the module answers "has this organisation bought it?", the
  // permission answers "may this user do it?". Both are carried on every route and
  // neither substitutes for the other.
  //
  // Five keys rather than the usual view/manage pair, because four genuinely
  // different jobs happen on one page:
  //
  //   .roster   — WHO signed up. A resident's participation in a named activity is
  //               personal data; the person who edits the calendar does not
  //               automatically need the attendance list.
  //   .enroll   — signing someone up or unsubscribing them ON THEIR BEHALF, and
  //               promoting from the waitlist. That is acting for a resident, which
  //               is a different act from planning.
  //   .pricing  — setting a price. Separate BECAUSE IT IS MONEY: it decides what
  //               lands on an apartment's bill, and it is required IN ADDITION TO
  //               .manage, never instead of it.
  //
  // ⚠ NONE of these goes into ROLE_DEFAULT_PERMISSIONS or into any permission
  // template. A template apply must not silently hand a customer's existing staff a
  // capability nobody asked for (CLAUDE.md, closed by default).
  activities: [
    "activities.view",
    "activities.manage",
    "activities.roster",
    "activities.enroll",
    "activities.pricing"
  ],
  // ── Plaatsbeschrijvingen (20260901-bewoner-onboarding/plaatsbeschrijving.md §8) ──
  //
  // Five keys, because these are genuinely different acts and two of them are
  // irreversible in ways .perform is not:
  //
  //   .publish    — publication FREEZES the document: rooms, items, statuses, photos,
  //                 meter readings, pins, signatures and the rendered PDF all become
  //                 immutable, and a correction afterwards is a NEW report, never an
  //                 edit. A report is a contract between parties; the act of making one
  //                 binding is not the same act as filling it in.
  //   .templates  — ONE edit changes every future report at the company. The person who
  //                 inspects a flat does not thereby decide what every colleague will be
  //                 asked to check next year.
  //
  // Same reasoning that separated activities.pricing from activities.manage.
  //
  // ⚠ NONE of these goes into ROLE_DEFAULT_PERMISSIONS or into any permission template.
  inspections: [
    "inspections.view",
    "inspections.perform",
    "inspections.publish",
    "inspections.templates",
    "inspections.export"
  ],
  // ── Customer onboarding (20260819-registrationandonboarding, spec-01) ─────
  // ALL THREE are PROTECTED_PERMISSIONS, and that is the entire feature.
  //
  // The flow ends with a self-nominated first `company_admin`. `company_admin` is
  // inside FULL_ACCESS_ROLES, so a key added the ordinary way is inherited by every
  // customer's own admin — including the one who just applied. Without the carve-out
  // below, an applicant could approve their own organisation, which is precisely the
  // gate the customer asked for ("MAAR ze mogen pas ... nadat we ... de klant ook
  // echt goedgekeurd hebben. Dat mogen enkel admins doen").
  //
  // `.view` is additionally CROSS-TENANT by nature: the queue lists organisations
  // that are not customers yet. It must never reach anyone outside CARO.
  onboarding: ["onboarding.invite", "onboarding.view", "onboarding.approve"],
  // ── Permission templates (20260824-permission-templates, LD-4) ────────────
  // Role defaults, moved out of code and into data an admin can edit.
  //
  // `.manage` is PROTECTED, and the split between these two keys is the whole
  // point. A GLOBAL baseline (companyId IS NULL) ships to EVERY tenant, so
  // editing one is a cross-tenant act. `company_admin` is inside
  // FULL_ACCESS_ROLES, so a non-protected key here would be inherited by every
  // customer's own admin — handing them the permission set of every other
  // customer's staff. Same structural argument as onboarding.* above.
  //
  // `.edit` is deliberately NOT protected: it covers cloning a baseline into an
  // override scoped to your OWN company, which is exactly what a customer admin
  // is supposed to be able to do (LD-2). The server still checks the row's
  // companyId — the key alone never authorises touching a global baseline.
  //
  // Neither key can grant anything its holder does not already hold: a template
  // may never contain a PROTECTED_PERMISSIONS key (LD-5, enforced 422
  // server-side), so templates cannot be used to launder a GDPR grant past the
  // typed-name confirmation.
  templates: ["templates.manage", "templates.edit"],
  // ── Bewonersmeldingen (20260831-domotica-meldingen-bewonerstablet, §11.1) ─
  //
  // Vier sleutels omdat er vier verschillende handelingen op één onderwerp zitten, en de
  // laatste twee zijn geen gradatie van de eerste twee.
  //
  // `.manage` is PROTECTED (OQ-8, 2026-08-31). Die sleutel bepaalt indirect WELKE
  // DOMOTICA-OUTPUT een bewoner mag schakelen: wie de acties van een melding configureert,
  // kiest de knop die op de tablet verschijnt. Ongeveer een kwart van de Qbus-outputs is
  // brand, paniek of een deurslot dat er in de datalaag uitziet als een lamp.
  // `company_admin` zit in FULL_ACCESS_ROLES en zou de sleutel zonder deze bescherming op
  // dag één bij elke klant erven.
  //
  // `.send` staat bewust apart van `.edit`: handmatig een volledig scherm op de tablet van
  // een bewoner duwen is een andere bevoegdheid dan een automation configureren. De eerste
  // is een handeling in iemands woonkamer, de tweede een instelling.
  //
  // ⚠ GEEN van deze vier gaat in ROLE_DEFAULT_PERMISSIONS of in een permissietemplate.
  // Role defaults zijn een STAMP: een template-uitbreiding verspreidt zich bij de volgende
  // apply naar élke bestaande klant. Vandaag houden 72 niet-CARO-accounts `devices.view`
  // via precies dat mechanisme.
  resident_notifications: [
    "resident_notifications.view",
    "resident_notifications.edit",
    "resident_notifications.manage",
    "resident_notifications.send"
  ],
  // 2026-09-03. De huisstijl van een organisatie of van één van haar gebouwen.
  //
  // EEN sleutel voor beide sporten, met de scope-controle in de handler (LD-3): wie hem
  // op siteniveau heeft past zijn eigen gebouw aan, op bedrijfsniveau hoort hij bij
  // company_admin. Twee sleutels zouden naast elkaar toegekend worden en niets
  // toevoegen — dezelfde afweging als bij `buttons.assign`, dat er om die reden niet is.
  //
  // NIET PROTECTED: de cascade is per tenant gescoped. Maar aan GEEN ENKELE rol
  // standaard toegekend — wie de huisstijl kan wijzigen, wijzigt wat er op de kassa en
  // op 800+ bewonerstablets staat.
  //
  // De CARO-standaarden zijn met opzet NIET bewerkbaar. Ze staan in code
  // (services/brandingService.js CARO_DEFAULTS): een klant die de fallback kan wijzigen,
  // wijzigt hem voor iedereen die zelf niets heeft ingesteld.
  branding: ["branding.manage"],
  // -- Checklistsjablonen voor de BEWONERSintrede (20260903-onboarding-maatwerk/spec-02)
  //
  // LET OP: dit is NIET de `onboarding.*`-namespace hierboven. Die gaat over
  // KLANTregistratie, is volledig PROTECTED en CARO-only. Deze sleutel is het
  // tegenovergestelde: hij hoort bij de klant zelf.
  //
  // Spec-02 LD-9 noemt hem `onboarding.templates`. Dat is hier bewust NIET overgenomen.
  // Een vierde sleutel in die namespace zou de opmerking erboven ("ALL THREE are
  // PROTECTED_PERMISSIONS, and that is the entire feature") meteen onwaar maken, en de
  // volgende lezer zou aannemen dat deze ook CARO-only is. Twee dingen die "onboarding"
  // heten in een permissielijst is precies hoe drift begint.
  //
  // Waarom een EIGEN sleutel en niet `persons.edit`: een bewerking hier verandert elke
  // TOEKOMSTIGE intrede van de organisatie. Wie een bewoner inschrijft, beslist daarmee
  // niet wat elke collega volgend jaar gevraagd wordt af te vinken. Dezelfde redenering
  // die `inspections.templates` van `inspections.perform` scheidde.
  //
  // Niet PROTECTED (de sjablonen zijn per tenant gescoped), maar aan GEEN ENKELE rol
  // standaard toegekend en in GEEN ENKELE permissietemplate.
  resident_onboarding: ["resident_onboarding.templates"],
  // -- Intakeformulieren en -gesprekken (20260903-onboarding-maatwerk/spec-03) -----
  //
  // VIJF sleutels, want het zijn vijf verschillende handelingen en drie ervan zijn
  // onomkeerbaar op een manier die .conduct niet is:
  //
  //   .templates     de VRAGEN bepalen. Een bewerking raakt elke toekomstige intake.
  //   .publish       een versie bevriezen en live zetten (O-8). Bewerken raakt niets;
  //                  publiceren raakt alles wat er daarna op gevoerd wordt. Dezelfde
  //                  splitsing als inspections.templates / inspections.publish.
  //   .medical_read  de MEDISCHE antwoorden lezen. Zonder deze sleutel toont een intake
  //                  wel DAT er medische antwoorden zijn en niet WAT -- een veld helemaal
  //                  verbergen laat iemand denken dat er niets gevraagd is, en dat is een
  //                  andere onwaarheid dan "ik mag dit niet zien".
  //
  // Elke lezing van een medisch antwoord schrijft een auditrij MET de lezer en ZONDER de
  // inhoud. Dat is niet symbolisch: het is de enige manier om na een klacht te zeggen wie
  // de gezondheidsgegevens van een bewoner heeft ingekeken.
  //
  // NIET PROTECTED: de formulieren zijn per tenant gescoped, en D-2 wil de
  // FULL_ACCESS_ROLES-bypass expliciet. Maar aan GEEN ENKELE rol standaard toegekend en in
  // GEEN ENKELE permissietemplate.
  intake: [
    "intake.view",
    "intake.conduct",
    "intake.templates",
    "intake.publish",
    "intake.medical_read"
  ],
  // -- Uittrede (20260901-bewoner-onboarding/offboarding.md LD-14, T-7.01) --------
  //
  // EEN sleutel, en hij verdient zichzelf. Een residentie beeindigen is geen
  // "een persoon bewerken": het ontkoppelt facturatie, trekt een tablet in en sluit een
  // hoofdstuk dat alleen met de hand heropend kan worden. Wie een bewoner mag inschrijven
  // (`persons.create`) of diens telefoonnummer mag corrigeren (`persons.edit`) beslist
  // daarmee niet dat die bewoner er niet meer woont.
  //
  // NIET PROTECTED (T-7.04): dit is een gewone operationele handeling voor de
  // sitebeheerder, geen CARO-voorbehouden bevoegdheid. Maar aan GEEN ENKELE rol standaard
  // toegekend en in GEEN ENKELE permissietemplate -- `persons.*` staat daar met opzet ook
  // niet in, en deze sleutel voegt zich daarbij.
  residents: ["residents.offboard"],
  // DeviceUser allowlist — see scripts/snapshot-device-user-perms.js.
  // Added back as their own group so they don't accidentally grow.
  models: ["models.view"],
  routes: ["routes.view"]
};
var PERMISSION_KEYS = Object.values(PERMISSION_GROUPS).flat();
var PERMISSION_DESCRIPTIONS = {
  login: "Sign in to the portal.",
  "dashboard.view": "View the home dashboard.",
  "audit.view": "Read the audit trail: sign-ins, refused sign-ins, and who changed or viewed what. Shows named staff activity, so it is a monitoring capability over colleagues.",
  "audit.export": "Download audit results as a file. The export leaves our retention and access controls behind, so it is granted separately from reading.",
  "alarms.view": "View alarms (active, history, details).",
  "alarms.create": "Manually create an alarm.",
  "alarms.edit": "Update alarm state or details.",
  "alarms.delete": "Delete alarms and bulk-delete.",
  "alarm_flows.view": "View alarm flows and their templates.",
  "alarm_flows.edit": "Edit existing alarm flows.",
  "alarm_flows.manage": "Create, delete, bind, and test alarm flows.",
  "automations.view": "View automation flows and execution history.",
  "automations.edit": "Toggle automations and edit existing flows.",
  "automations.manage": "Create, delete, test, and replay automations.",
  "qbus.view": "View Qbus controllers, outputs, and configuration.",
  "qbus.edit": "Edit Qbus outputs (rename, reassign, folder management, bulk).",
  "qbus.manage": "Send commands, refresh hubs, manage icon mappings, control outputs, classify output-name prefixes, and open outputs to residents.",
  "telephony.view": "View SIP endpoints, sessions, call logs, and DID mappings.",
  "telephony.edit": "Edit SIP endpoint configuration.",
  "telephony.manage": "Provision, regenerate, and delete telephony resources.",
  "firmware.view": "View firmware versions.",
  "firmware.create": "Upload new firmware versions.",
  "firmware.edit": "Update firmware version metadata.",
  "firmware.delete": "Delete firmware versions.",
  "services.view": "View service definitions, versions, and update paths.",
  "services.manage": "Create, edit, and delete service definitions and update paths.",
  "device_types.view": "View device-type definitions.",
  "device_types.manage": "Create, edit, and delete device types.",
  "device_linking.manage": "Link and unlink devices to physical hardware.",
  "baseip.manage": "Manage Base IP integration configuration.",
  "enocean.view": "View EnOcean buttons and hubs.",
  "enocean.edit": "Edit EnOcean buttons, run learning sessions, post device events.",
  "enocean.import": "Bulk-import EnOcean buttons from spreadsheets.",
  "eink.manage": "Design and push e-ink display templates.",
  "indoor.view": "View floor plans, points of interest, and wayfinding routes.",
  "indoor.manage": "Edit floor plans, POIs, entrances, wayfinding nodes, and navigation cache.",
  "monitoring.view": "View monitoring dashboards, ping history, and metrics.",
  "monitoring.manage": "Declare that a fix for an observability finding has been deployed. Starts a measurement rather than closing anything; granted to developer only, because the failure mode is reporting a live defect as fixed.",
  "unit_audit.view": "View unit audit reports.",
  "unit_audit.manage": "Edit the organisation-wide unit audit profile: which device types every apartment must contain. Own organisation only; platform admins may edit any.",
  "notifications.view": "Receive notifications and edit own notification preferences.",
  "notifications.manage": "Send admin notifications and manage system-wide notification settings.",
  "settings.view": "View own settings.",
  "system.admin": "Full system administration (impersonation, business updates, sales presentation, model/route/migration builder, company-management).",
  "system.logs": "View and export server logs.",
  "integrations.manage": "Manage Clearvox, 2N, Hexnode, UniFi, and other 3rd-party integrations.",
  "emergency_services.manage": "Manage emergency-service providers and integrations.",
  "devices.view": "View devices.",
  "devices.create": "Register and create new devices.",
  "devices.edit": "Edit device configuration.",
  "devices.delete": "Delete devices.",
  "devices.manage": "Bulk operations and lifecycle management.",
  "devices.admin": "Run admin commands on a device (kill, uninstall service, force-register), and push caro-agent firmware updates from /firmware-management.",
  "devices.bulk-action": "Run bulk actions across many devices at once.",
  "devices.control_2n": "Physically unlock a door on a 2N intercom. A separate key from devices.edit on purpose: this opens a door in a care facility. Granted to no role by default.",
  "devices.restart": "Reboot a 2N intercom. Acts on a live device in a resident home; granted to no role by default.",
  "devices.test_call": "Place a test call from a 2N intercom. Acts on a live device in a resident home; granted to no role by default.",
  "locations.view": "View locations.",
  "locations.tenant_repair": "PROTECTED. See locations that have no company and assign one. Cross-tenant by nature \u2014 the list spans every customer. CARO only, granted explicitly to named accounts.",
  "locations.create": "Create new locations.",
  "locations.edit": "Edit existing locations.",
  "locations.phone_numbers": "Verify and correct the phone number (data.phoneNumber) that alarm flows and the videophone test dial for each apartment, per site. Granted to no role by default.",
  "intake.view": "See intake interviews and their answers (medical answers stay hidden without intake.medical_read).",
  "intake.conduct": "Run an intake interview with a resident and record the answers.",
  "intake.templates": "Build the questionnaire: fields, groups, grids and branching. ONE edit changes every FUTURE intake; running interviews keep the version they started on. Granted to no role by default.",
  "intake.publish": "Freeze a draft version and make it live. Editing changes nothing on its own; publishing decides what every future interview asks. Granted to no role by default.",
  "intake.medical_read": "Read the MEDICAL answers of an intake. Without it the interview still shows THAT medical answers exist, but not what they say. Every read writes an audit row naming the reader and never the content. Granted to no role by default.",
  "residents.offboard": "End a residency: announce a departure date, close it, or move a resident to another apartment. Detaches billing, revokes the tablet token and restores the device to the apartment. History is kept and invoicing stays possible \u2014 but reopening a closed residency is a manual act. Granted to no role by default.",
  "resident_onboarding.templates": "Edit the move-in and move-out checklist templates of the organisation or of one of its buildings. ONE edit changes every FUTURE move-in; running ones keep the snapshot they started with. Granted to no role by default. NOT the same as onboarding.* (customer registration, CARO only).",
  "branding.manage": "Set the logo, colours, title and font for an organisation or for one of its buildings. Resolution is per field: a building that only sets a logo keeps the organisation colours. Granted to no role by default \u2014 it changes what the till and 800+ resident tablets show.",
  "locations.delete": "Delete locations.",
  "locations.manage": "Manage location access, types, and reference data (incl. import).",
  "tickets.view": "View tickets and their messages.",
  "tickets.create": "Create new tickets and post messages/attachments.",
  "tickets.edit": "Edit existing tickets, request interventions, manage solutions.",
  "tickets.delete": "Delete tickets.",
  "tickets.manage": "Manage known issues, solutions, and ticket admin (assign/close).",
  "tickets.assign": "Assign tickets to other users.",
  "tickets.close": "Close or reopen tickets.",
  "camera_requests.view": "View camera footage requests.",
  "camera_requests.create": "Create new camera footage requests.",
  "camera_requests.approve_site": "Approve site-level camera footage requests.",
  // GDPR-protected — admin role does NOT auto-grant. See PROTECTED_PERMISSIONS.
  "camera_requests.approve_dpo": "Approve DPO-level (GDPR) camera footage requests. Requires explicit grant \u2014 admin bypass disabled.",
  "camera_requests.retrieve_footage": "Retrieve and download approved camera footage.",
  // ── 20260831-cameras-als-toestel ─────────────────────────────────────────
  // Live beeld, niet opgenomen beeld. Zie de opmerking bij de `cameras`-groep.
  "cameras.view": "View cameras, their health, and live streams.",
  "cameras.edit": "Create and edit cameras and their credentials.",
  "cameras.manage": "Delete cameras.",
  "cameras.manage_links": "PROTECTED. Decide which apartments may view which camera. Requires explicit grant \u2014 admin bypass disabled.",
  "field_interventions.view": "View own field-intervention assignments.",
  "field_interventions.view_all": "View all field interventions.",
  "field_interventions.create": "Create new field interventions.",
  "field_interventions.edit": "Edit field-intervention details.",
  "field_interventions.delete": "Delete field interventions.",
  // ── 20260818-invoicingsystem ─────────────────────────────────────────────
  "modules.view": "See which paid modules your own organisation has.",
  // PROTECTED — admin role does NOT auto-grant. See PROTECTED_PERMISSIONS.
  "modules.manage": "Enable or disable paid modules for a company. CARO-only commercial action \u2014 admin bypass disabled.",
  "invoicing.view": "View invoices and billable items. Does NOT reveal amounts.",
  // PROTECTED — admin role does NOT auto-grant. See PROTECTED_PERMISSIONS.
  "invoicing.view_amounts": "See monetary amounts on invoices and billable items. Requires explicit grant \u2014 admin bypass disabled.",
  "invoicing.manage": "Create draft invoices, enter billable items by hand, manage billing parties.",
  "invoicing.issue": "Issue, send and credit-note invoices. Consumes invoice numbers.",
  "invoicing.payments": "Record payments and allocate them to invoices.",
  "invoicing.config": "Configure the invoice issuer identity and the accounting subdivisions.",
  "invoicing.export": "Export a period of invoices and lines for the accountant.",
  "pos.view": "View cafeteria sales and daily closes.",
  "pos.sell": "Ring up sales at a till.",
  "pos.catalog": "Manage the per-site cafeteria catalog and till configuration.",
  "pos.restrictions": "Manage per-person product restrictions (for example alcohol).",
  "pos.override_restriction": "Override a product restriction at the till, recorded on the sale line with your name.",
  "pos.reports": "Run cafeteria reports per site, period, product and cashier.",
  // PROTECTED — admin role does NOT auto-grant. See PROTECTED_PERMISSIONS.
  "pos.report_persons": "See a named resident's itemised consumption. Requires explicit grant \u2014 admin bypass disabled.",
  "pos.cash_count": "Close a cafeteria day and record the counted cash.",
  "pos.terminals": "Provision and revoke till terminals.",
  "pos.export": "Export cafeteria sales and day closes for a period.",
  "agenda.view": "View the shared agenda (company, site and unit tiers) in the portal. Does NOT reveal residents' private entries \u2014 those are stripped server-side for everyone except the owner and CARO staff, and no permission key grants them.",
  "agenda.manage": "Create, edit and delete shared agenda entries at any tier. Still never reaches a resident's private entry.",
  "activities.view": "View the activities catalog and the dates of each series. Does NOT reveal who signed up.",
  "activities.manage": "Create, edit, archive and cancel activities, generate their dates, and edit the per-site activity settings. Does NOT allow setting a price \u2014 that is activities.pricing.",
  "activities.roster": "See who signed up for a date. A resident's participation in a named activity is personal data, so it is granted separately from planning the activity.",
  "activities.enroll": "Sign a resident up or unsubscribe them on their behalf, and promote someone from the waitlist. Acting for a resident is a different act from planning.",
  "activities.pricing": "Set the price of an activity. Separate because it is money: it decides what lands on an apartment's bill. Required IN ADDITION TO activities.manage, never instead of it.",
  "inspections.view": "See condition reports (plaatsbeschrijvingen) and the archive, including their photos. A published report contains photographs of a named resident's home, so every read is audited.",
  "inspections.perform": "Create and fill in a condition report: room and item statuses, photos, floor-plan pins, meter readings, signatures, and sending one for online confirmation. Does NOT allow publishing \u2014 that is inspections.publish.",
  "inspections.publish": "Publish a condition report, which FREEZES it permanently: after this every field is immutable and a correction is a new report. Separate from inspections.perform because publication is the act that turns a form into evidence, and it cannot be undone.",
  "inspections.templates": "Edit the room and checklist templates every future report at this company is built from. One edit changes what all colleagues are asked to check; existing reports are unaffected because they snapshot their template at creation.",
  "inspections.export": "Download a report's PDF and bulk-export the archive. A separate column from read on purpose: an export leaves our retention and access controls behind.",
  "catalog.view": "Browse the device catalog / Shop and view tax rates. Does NOT reveal prices.",
  "catalog.view_prices": "See item prices in the Shop. Grant alongside catalog.view; catalog.manage already implies it.",
  "catalog.manage": "Create, edit, deactivate catalog items; set stock; append prices; edit the tax matrix.",
  "estimates.view": "View repair estimates inside tickets.",
  "estimates.create": "Build and edit draft estimates (device and non-catalog lines).",
  "estimates.offer": "Offer an estimate to the client (freeze figures + send the approval link).",
  "estimates.bill_without_approval": "Finalize a contract-covered intervention cost without prior approval.",
  "knowledge_base.view": "View the portal Knowledge Base (help center). Reserved for v2 role-scoping; granted to all roles in v1.",
  "knowledge_base.manage": "Author manual help pages, override/lock/revert auto-generated sections, roll back revisions, and trigger a manual regeneration run.",
  "site_board.manage": "Provision and revoke corridor alarm panels, rotate a site\u2019s unlock code, and clear a site lockout. Does not grant reading an existing code \u2014 codes are hashed and unreadable by design.",
  "persons.view": "View resident/person records.",
  "persons.create": "Create resident/person records.",
  "persons.edit": "Edit resident/person records.",
  "persons.delete": "Delete resident/person records.",
  "persons.invite": "Invite a person/family member to the resident app.",
  "persontypes.manage": "Manage person-type reference data.",
  "users.view": "View users in the same company.",
  "users.edit": "Edit user accounts.",
  "users.manage": "Create, deactivate, and delete users.",
  "users.permissions.manage": "Edit any user\u2019s permissions and impersonate them.",
  "company.view": "View company profile.",
  "company.edit": "Edit company profile.",
  "company.manage": "Manage companies (cross-company operations).",
  "company.settings.manage": "Manage per-company settings.",
  "files.upload": "Upload new files.",
  "files.delete": "Delete files and folders.",
  "files.share": "Create file shares.",
  "files.manage": "Manage file storage system-wide.",
  // Documentclassificatie (specs/20260902-documentclassificatie §6). Reading a file is the
  // engine's answer, never a key; the six read keys are the matrix form of a topic grant.
  "documents.read.medical": "Read documents in the medical topic, up to vertrouwelijk. Stamping this writes a topic grant; strikt needs a personal grant.",
  "documents.read.financial": "Read documents in the financial topic (invoices, statements), up to vertrouwelijk.",
  "documents.read.legal": "Read documents in the legal topic (contracts, correspondence), up to vertrouwelijk.",
  "documents.read.technical": "Read documents in the technical topic (device, location and ticket documents), up to vertrouwelijk.",
  "documents.read.housing": "Read documents in the housing topic (leases, inspections, floor plans), up to vertrouwelijk.",
  "documents.read.hr": "Read documents in the hr topic (staff material), up to vertrouwelijk.",
  "documents.classify": "Downgrade a document label, change its topics, or move it to a looser path \u2014 each with a reason, each audited. Upgrading needs no key. Also reveals the count of withheld documents in a folder.",
  "documents.access_admin": "PROTECTED. Grants page: manual grants, strikt grants (typed confirmation), 72-hour emergency grants and recertification. May not be held together with documents.exclusion_admin \u2014 refused server-side with the pair named.",
  "documents.exclusion_admin": "PROTECTED. Exclusions page: exclude a named user from a document, a subtree or a topic for a bounded period, with a reason. May not be held together with documents.access_admin.",
  "documents.retention": "PROTECTED. Archiving page: the retention proposals per customer, legal hold, and the audited batch deletion. Nothing is deleted automatically.",
  "reports.view": "View reports.",
  "reports.export": "Export reports.",
  "ideas.view": "View the Ideabox board, the roadmap and idea detail.",
  "ideas.create": "Submit a new idea.",
  "ideas.vote": "Up-vote an idea. Separate from ideas.view so abuse can be revoked without blinding someone.",
  "ideas.triage": "PROTECTED. Publish, redact, decline or merge a submitted idea. This is the data-protection gate in front of an AI processor and an email fan-out \u2014 CARO only, granted by migration to named accounts.",
  "ideas.manage": "PROTECTED. Send an idea to the AI, place it on the roadmap, mark it shipped, and answer its open questions. CARO only, granted by migration to named accounts.",
  "onboarding.invite": "PROTECTED. Issue and revoke the one-time link that opens the customer onboarding form (gate 1). CARO only, granted explicitly to named accounts.",
  "onboarding.view": "PROTECTED. See the onboarding queue and submitted organisation data. CROSS-TENANT by nature \u2014 it lists organisations that are not customers yet. CARO only.",
  "onboarding.approve": "PROTECTED. Approve an application (creates the company and its first company_admin, and unlocks sign-in) or reject it with a reason. Gate 2. CARO only \u2014 the applicant must never inherit this.",
  "templates.manage": "PROTECTED. Create, edit and delete GLOBAL permission templates \u2014 the role-default baselines that ship to every tenant. Cross-tenant. CARO only, granted explicitly to named accounts.",
  "templates.edit": "Clone a global permission template into an override scoped to your own company, and edit that override. Never reaches a global baseline.",
  "resident_notifications.view": "View resident notifications, their per-device delivery status, and the sound library.",
  "resident_notifications.edit": "Edit notification content and upload notification sounds.",
  "resident_notifications.manage": "Delete notifications and sounds, and configure notification actions that control domotics outputs.",
  "resident_notifications.send": "Send an ad-hoc notification to a resident tablet outside of an automation.",
  "models.view": "View DB models. Used by DeviceUser for service-script sync.",
  "routes.view": "View API route metadata. Used by DeviceUser for service-script sync."
};
var FULL_ACCESS_ROLES = ["admin", "company_admin"];
var PROTECTED_PERMISSIONS = [
  "camera_requests.approve_dpo",
  "ideas.triage",
  "ideas.manage",
  // 20260818-invoicingsystem (D22, D25). Each of these would otherwise be handed
  // to every customer's own admin by the FULL_ACCESS_ROLES short-circuit:
  //   modules.manage        — self-granting a paid module
  //   invoicing.view_amounts— resident financial data
  //   pos.report_persons    — a named resident's itemised consumption
  "modules.manage",
  "invoicing.view_amounts",
  "pos.report_persons",
  // 20260819-registrationandonboarding (D7 / LD-ONB-PROTECTED). The onboarding flow
  // MINTS a `company_admin` — so the role that would inherit these keys is created by
  // the very action they guard. Without these three entries an applicant approves its
  // own organisation:
  //   onboarding.invite   — issuing the link that opens the form (gate 1)
  //   onboarding.view     — the CROSS-TENANT queue of organisations that are not
  //                         customers yet
  //   onboarding.approve  — creating the company + first admin, and unlocking sign-in
  //                         (gate 2)
  "onboarding.invite",
  "onboarding.view",
  "onboarding.approve",
  // 20260824-permission-templates (LD-4, corrected from the spec's first draft).
  // A GLOBAL permission template (companyId IS NULL) ships to every tenant, so
  // editing one is cross-tenant. Unprotected, the FULL_ACCESS_ROLES short-circuit
  // would hand it to every customer's own `company_admin` — who could then rewrite
  // the default permission set every other customer's staff is stamped with.
  // `templates.edit` is deliberately absent: it is scoped to your own company.
  "templates.manage",
  // 20260824 tenant-isolation repair. The orphan-location screen lists locations with
  // no company across EVERY customer and assigns tenancy to them. Cross-tenant read
  // AND write, so it must never reach a customer's own admin by omission.
  "locations.tenant_repair",
  // 2026-08-25 NIS2 audit reporting. The audit trail names which member of staff
  // opened which resident record and when — a monitoring view over colleagues, and
  // personal data in its own right. `company_admin` is in FULL_ACCESS_ROLES, so
  // without these entries every customer's own admin would hold it on day one.
  "audit.view",
  "audit.export",
  // ── 20260831-cameras-als-toestel: `cameras.manage_links` staat hier BEWUST NIET ──
  //
  // OQ-5 beantwoordde ik oorspronkelijk met "beschermen": beslissen wie er meekijkt is
  // een privacybeslissing, en `company_admin` zit in FULL_ACCESS_ROLES en zou hem dus bij
  // elke klant op dag één erven.
  //
  // BESLISSING VAN DE EIGENAAR, 2026-09-03: nee. De `admin`-rol is het hoogste niveau in
  // dit platform en hoort elk recht automatisch te hebben; een beheerder die zijn eigen
  // camerakoppelingen niet kan leggen, is een gate die alleen de eigenaar hindert.
  //
  // ⚠ Het gevolg is wél breder dan alleen `admin`: `company_admin` staat ook in
  // FULL_ACCESS_ROLES, dus de admin van elke KLANT krijgt hiermee ook het recht om te
  // bepalen welke appartementen welke camera zien. Wil je dat laatste niet, dan is de
  // oplossing niet deze lijst maar `company_admin` uit FULL_ACCESS_ROLES halen — en dat
  // is een veel grotere ingreep (zie project_company_admin_full_access).
  // 20260831-domotica-meldingen-bewonerstablet (OQ-8). Wie de ACTIES van een melding mag
  // configureren, kiest welke domotica-output een bewoner met één druk kan schakelen —
  // en ongeveer een kwart van de Qbus-outputs is brand, paniek of een deurslot dat er in
  // de datalaag identiek uitziet als een lamp. Dat is een fysieke-veiligheidsbeslissing,
  // geen beheertaak. `company_admin` zit in FULL_ACCESS_ROLES en zou hem zonder deze regel
  // op dag één bij elke klant erven.
  //
  // De andere drie zijn bewust NIET beschermd: meldingen bekijken, tekst schrijven en een
  // losse melding versturen blijven gewone beheerhandelingen, elk met een auditrij.
  "resident_notifications.manage",
  // 20260902-documentclassificatie (spec §6, LD-5 stap 4). Drie beheersleutels, elk een
  // privacy- of bewijsbeslissing over andermans documenten, en `company_admin` zit in
  // FULL_ACCESS_ROLES:
  //   documents.access_admin    — handmatige grants, `strikt`-grants en noodgrants uitdelen;
  //                               zonder deze regel geeft elke klant-admin zichzelf op dag één
  //                               een `strikt`-grant en is R4b's stop bij `vertrouwelijk` dood
  //   documents.exclusion_admin — een collega uitsluiten van een dossier, een boom of een topic
  //   documents.retention       — de archiveringspagina: batch-verwijdering en legal hold
  // `documents.classify` en de zes `documents.read.*` zijn bewust NIET beschermd: het zijn
  // tenant-gescoopte werkrechten die een template mag dragen.
  "documents.access_admin",
  "documents.exclusion_admin",
  "documents.retention"
];
var PERMISSION_INCOMPATIBLE_PAIRS = [
  ["documents.access_admin", "documents.exclusion_admin"]
];
var NEW_VIEW_KEYS_FOR_MANAGERS = [
  "dashboard.view",
  "alarms.view",
  "alarm_flows.view",
  "automations.view",
  "qbus.view",
  "telephony.view",
  "firmware.view",
  "services.view",
  "device_types.view",
  "enocean.view",
  "indoor.view",
  "monitoring.view",
  "unit_audit.view",
  "notifications.view",
  "settings.view"
];
var ALARM_STANDARD_KEYS = [
  "alarms.view",
  "alarms.create",
  "alarms.edit"
];
function uniq(keys) {
  return [...new Set(keys)];
}
var ROLE_DEFAULT_PERMISSIONS = {
  // FULL_ACCESS_ROLES bypass via short-circuit; defaults are informational.
  admin: [],
  super_admin: [],
  company_admin: [],
  // Legacy defaults preserved verbatim from domain.ts; new keys appended.
  company_manager: uniq([
    "login",
    "devices.view",
    "devices.create",
    "devices.edit",
    "locations.view",
    "locations.create",
    "locations.edit",
    "tickets.view",
    "tickets.create",
    "tickets.edit",
    "tickets.delete",
    "camera_requests.view",
    "camera_requests.create",
    "camera_requests.approve_site",
    "field_interventions.view",
    "field_interventions.create",
    "field_interventions.edit",
    "users.view",
    "users.edit",
    "users.permissions.manage",
    "company.view",
    // files.view / files.download gepensioneerd 2026-09-03 (D-12, migratie 20260910100600).
    "files.upload",
    "files.share",
    "reports.view",
    "reports.export",
    // New keys (locked decision 2026-04-28 — merged 2026-04-29).
    ...NEW_VIEW_KEYS_FOR_MANAGERS,
    ...ALARM_STANDARD_KEYS,
    "automations.edit",
    "alarm_flows.edit",
    "telephony.edit",
    "qbus.edit",
    // Repair estimates & catalog — admin/manager tier (CR-6/CR-12).
    // Manager tier sees prices by default (decision 2026-07-17).
    "catalog.view",
    "catalog.view_prices",
    "catalog.manage",
    "estimates.view",
    "estimates.create",
    "estimates.offer",
    "estimates.bill_without_approval",
    // Knowledge Base — manager tier can author/override (LD-PERMS).
    "knowledge_base.view",
    "knowledge_base.manage",
    // Portal Ideabox — participation only. NEVER .triage or .manage: those are
    // PROTECTED and belong to named CARO accounts by migration (LD-TRIAGE-CARO).
    "ideas.view",
    "ideas.create",
    "ideas.vote",
    // Paid modules — see which ones this organisation has. NEVER .manage (PROTECTED).
    "modules.view",
    // Resident invoicing (20260818-invoicingsystem, D22). Manager tier runs the whole
    // billing loop. `invoicing.view_amounts` is deliberately ABSENT — PROTECTED, and it
    // is the one key a go-live must remember to grant explicitly.
    "invoicing.view",
    "invoicing.manage",
    "invoicing.issue",
    "invoicing.payments",
    "invoicing.config",
    "invoicing.export",
    // Cafeteria POS. `pos.report_persons` is deliberately ABSENT — PROTECTED.
    "pos.view",
    "pos.sell",
    "pos.catalog",
    "pos.restrictions",
    "pos.override_restriction",
    "pos.reports",
    "pos.cash_count",
    "pos.terminals",
    "pos.export"
  ]),
  site_manager: uniq([
    "login",
    "devices.view",
    "devices.edit",
    "locations.view",
    "locations.edit",
    "tickets.view",
    "tickets.create",
    "tickets.edit",
    "camera_requests.view",
    "camera_requests.create",
    "camera_requests.approve_site",
    "field_interventions.view",
    "field_interventions.create",
    "users.view",
    "users.edit",
    "company.view",
    // files.view / files.download gepensioneerd 2026-09-03 (D-12, migratie 20260910100600).
    "files.upload",
    "reports.view",
    // New keys.
    ...NEW_VIEW_KEYS_FOR_MANAGERS,
    ...ALARM_STANDARD_KEYS,
    "qbus.edit",
    "enocean.edit",
    // Repair estimates & catalog — admin/manager tier (CR-6/CR-12).
    // Manager tier sees prices by default (decision 2026-07-17).
    "catalog.view",
    "catalog.view_prices",
    "catalog.manage",
    "estimates.view",
    "estimates.create",
    "estimates.offer",
    "estimates.bill_without_approval",
    // Knowledge Base — manager tier can author/override (LD-PERMS).
    "knowledge_base.view",
    "knowledge_base.manage",
    // Portal Ideabox — participation only. NEVER .triage or .manage.
    "ideas.view",
    "ideas.create",
    "ideas.vote",
    // Paid modules — visibility only. NEVER .manage (PROTECTED).
    "modules.view",
    // Resident invoicing. Site tier configures and bills but does not own the
    // organisation-level accounting config; `invoicing.config` is withheld here on
    // purpose, and `invoicing.view_amounts` is PROTECTED.
    "invoicing.view",
    "invoicing.manage",
    "invoicing.issue",
    "invoicing.payments",
    "invoicing.export",
    // Cafeteria POS — the site tier runs the till. `pos.report_persons` is PROTECTED.
    "pos.view",
    "pos.sell",
    "pos.catalog",
    "pos.restrictions",
    "pos.override_restriction",
    "pos.reports",
    "pos.cash_count",
    "pos.terminals",
    "pos.export"
  ]),
  // ── REDEFINED 2026-08-24, 26 keys -> 21 ───────────────────────────────────
  //
  // Spec: specs/20260824-rights-consistency-audit §7 (step 7). Design rule:
  // **a normal user is operational staff on a floor.** They see and act on what is
  // in front of them; they configure nothing, provision nothing, and see no money
  // and no other tenant.
  //
  // Five keys were removed, and every one of them was measured to gate NOTHING in
  // either repo before removal (grep over routes/ and packages/portal/src):
  //
  //   files.view, files.download  - /api/files is 9/9 ungated and /api/share has no
  //                                 auth middleware at all. These implied a control
  //                                 that does not exist. (Question Q-D, open since
  //                                 2026-04-28 — CLOSED 2026-09-03: both keys are
  //                                 retired from the catalog by
  //                                 specs/20260902-documentclassificatie D-12 and
  //                                 stripped from every envelope by migration
  //                                 20260910100600.)
  //   reports.view                - there is no reports router.
  //   pos.sell                    - the till authenticates by DEVICE TOKEN
  //                                 (posTerminalAuth), never by user permission.
  //                                 This key appears in no route gate anywhere, so
  //                                 it granted nothing and misrepresented the policy.
  //   pos.view                    - it is the `read` verb on five SUPERVISORY pages
  //                                 (catalog, config, restrictions, day-closes,
  //                                 terminals). Its only real effect was making
  //                                 those look openable in the permission matrix,
  //                                 including the day-close cash figures. Selling is
  //                                 unaffected: it is device-token authenticated.
  //
  // So this narrows what the template CLAIMS, not what a user can do. That was the
  // point: the old set read as more generous than it was.
  //
  // ⚠ THIS IS A STAMP, NOT LIVE INHERITANCE (LD-1). Existing users keep their stored
  // permissions; nothing changes for anyone until this template is applied to them.
  // And if `permission_template` has been seeded, the DATABASE is the source of truth
  // and this constant is the seed source only — the sibling migration
  // 20260824160000-redefine-user-template.js updates the row.
  //
  // `persons.view` remains absent, now deliberately rather than by omission: resident
  // PII should be an explicit grant. It was never in this set, so this is a no-op that
  // makes the intent visible.
  // ── THE PLAIN USER: TICKETS, LOCATIONS, ALARMS ────────────────────────────
  //
  // Narrowed 2026-08-24 on the owner's instruction: "vandaag zijn alle users (niet
  // company admin) enkel gebruik aan het maken van tickets, locaties en alarmen. Ze
  // hoeven niet meer te zien tenzij ik het zelf open zet."
  //
  // So the default is the three named areas, plus only those surfaces a person needs
  // to USE them: signing in, the landing page, their OWN notification preferences and
  // their OWN notification list, and the help centre. Everything a default should not
  // decide on someone's behalf is now an explicit grant an admin makes per user.
  //
  // Removed here, and each one re-grantable in one click on the Users screen:
  //   devices.view            - the device fleet is technical staff's work
  //   camera_requests.*       - camera access is a GDPR-sensitive request flow
  //   field_interventions.view- planning belongs to the people who schedule it
  //   company.view            - organisation data, not day-to-day work
  //   catalog.view            - the Shop
  //   modules.view            - which paid modules exist
  //   ideas.view/create/vote  - the Ideabox. NOTE: this reverses the 2026-08-17
  //       decision that "every portal role may submit and vote... the people who hit
  //       the friction are the ones who can report it". Reversed deliberately on the
  //       owner's instruction above, not by oversight. Re-granting it to everyone is
  //       one preset edit away if the Ideabox turns out to be missed.
  //
  // ⚠ Changing this list does NOT change any existing user. Permissions are STAMPED
  // onto the user record, never inherited live (LD-1). Existing accounts keep what
  // they hold until an admin applies the template to them from the Users screen.
  user: uniq([
    "login",
    "dashboard.view",
    // Tickets - view and raise. Not edit/manage/delete: those belong to whoever
    // owns the queue.
    "tickets.view",
    "tickets.create",
    "locations.view",
    // Alarms are standard equipment for every logged-in role (decision 2026-07-13):
    // whoever is around picks the alarm up. `alarms.delete` stays out - destroying
    // alarm history is an admin/audit concern.
    ...ALARM_STANDARD_KEYS,
    // Strictly personal surfaces: this user's own notification preferences and their
    // own notification list. Not other people's data, and withholding them makes
    // ticket work feel broken rather than restricted.
    "settings.view",
    "notifications.view",
    // Knowledge Base - reading is ungated anyway; view reserved for v2 scoping.
    "knowledge_base.view"
  ]),
  developer: uniq([
    ...NEW_VIEW_KEYS_FOR_MANAGERS,
    ...ALARM_STANDARD_KEYS,
    "system.logs",
    // Observability feedback loop (spec 20260807). Deliberately NOT in
    // NEW_VIEW_KEYS_FOR_MANAGERS: that list is view-only and is spread into
    // company_manager and site_manager too. Claiming a backend fix is deployed is a
    // developer's call, and getting it wrong reports a live defect as closed.
    "monitoring.manage",
    // Shop is reachable from every role's navigation (decision 2026-07-17);
    // prices stay behind an explicit grant.
    "catalog.view",
    // Knowledge Base — read access (LD-PERMS).
    "knowledge_base.view",
    // Portal Ideabox — participation only. ⚠ NEVER .triage or .manage here in
    // particular: `developer` is a role a CUSTOMER could hold, which is exactly why
    // the grant migration selects named accounts rather than a role.
    "ideas.view",
    "ideas.create",
    "ideas.vote"
  ])
};
function isProtectedPermission(key) {
  return PROTECTED_PERMISSIONS.includes(key);
}
function isFullAccessRole(role) {
  if (!role) return false;
  return FULL_ACCESS_ROLES.includes(role);
}

// src/constants/pageRegistry.ts
var PAGE_STANDARD_VERBS = ["read", "write", "manage"];
var PAGE_CUSTOM_VERBS = [
  "prices",
  "amounts",
  "persons",
  // Action suffixes that collide with an already-taken standard verb.
  "create",
  "delete",
  "admin",
  "bulk",
  "invite",
  "accounts",
  "all",
  "vote",
  "triage",
  "approve_dpo",
  "footage",
  "site_board",
  "bill",
  // Physically unlocking a door on a 2N intercom - see devices.control_2n.
  "door",
  "restart",
  "test_call",
  // Downloading audit results as a file - see audit.export. A separate column from
  // `read` on purpose: an export leaves our retention and access controls behind.
  "export",
  // 20260825-animatie-activiteiten-en-agenda §6. Three action suffixes on ONE page,
  // and all three collide with a standard verb that is already taken there:
  //   roster  — seeing WHO signed up (activities.view already holds `read`)
  //   enroll  — signing someone up ON THEIR BEHALF
  //   pricing — setting a price (activities.manage already holds `manage`)
  // Without these, verbForKey()'s `find` would render one of them and drop the other
  // two silently — no column, no checkbox, no warning. That is exactly how
  // firmware-management shipped two ungrantable keys.
  "roster",
  "enroll",
  "pricing",
  // 20260831-cameras-als-toestel. `cameras.manage_links` botst met `manage`, dat op de
  // camerapagina al door `cameras.manage` (verwijderen) bezet is. Zonder deze regel zou
  // verbForKey()'s `find` er één van de twee renderen en de andere stil laten vallen —
  // en dan is de ENIGE PROTECTED sleutel van deze feature onvindbaar in de matrix,
  // wat neerkomt op: niemand kan hem verlenen en niemand ziet dat hij bestaat.
  "manage_links",
  // 20260831-domotica-meldingen-bewonerstablet §11.1. `resident_notifications.send` botst
  // niet met een standaardwerkwoord maar bestaat ook niet als er niets voor gedeclareerd
  // is: verbForKey() rendert alleen kolommen die hier staan. Zonder deze regel is `.send`
  // onvindbaar in de matrix — niemand kan hem verlenen en niemand ziet dat hij bestaat,
  // precies zoals firmware-beheer twee onverleenbare sleutels opleverde.
  //
  // Het is een EIGEN kolom en geen synoniem van `write`: een melding schrijven is iets
  // anders dan hem op iemands scherm zetten.
  "send",
  // 20260901-bewoner-onboarding/plaatsbeschrijving.md §8. On the inspections pages
  // `read`, `write` and `manage` are already taken by .view, .perform and .publish, so
  // `templates` needs its own column or verbForKey()'s `find` renders one key and drops
  // the other silently — no column, no checkbox, no warning, which is exactly how
  // firmware-management shipped two ungrantable keys.
  //
  // `export` is already declared above (audit.export) and is reused here deliberately: it
  // means the same thing on both pages — a file that leaves our retention and access
  // controls behind.
  "templates",
  // `perform` is NOT a synonym of `write`: filling in a condition report and publishing
  // it are different acts, and `manage` is held by .publish on the same page.
  "perform",
  "publish",
  // 20260902-documentclassificatie (plan §6, P-11). Tien actie-suffixen op vier pagina's.
  // Op `/documents` staan ZEVEN sleutels naast elkaar en geen enkele is een read/write/manage:
  // lezen is daar het antwoord van de engine, geen sleutel (D-14). Elk topic krijgt zijn
  // eigen verb, zodat verbForKey()'s `find` er nooit één rendert en de andere vijf stil laat
  // vallen — precies hoe firmware-management twee ontoekenbare sleutels verscheepte.
  "medical",
  "financial",
  "legal",
  "technical",
  "housing",
  "hr",
  "classify",
  // De drie PROTECTED-beheerpagina's, elk met precies één sleutel en dus één verb.
  "access_admin",
  "exclusion_admin",
  "retention",
  // 20260901-bewoner-onboarding/offboarding.md LD-14 (T-7.01). `residents.offboard` staat op
  // de uittredepagina en die pagina heeft verder geen sleutels -- maar dat maakt een eigen
  // verb niet overbodig, het maakt hem noodzakelijk: verbForKey() rendert alleen kolommen
  // die hier staan, en zonder deze regel is de ENIGE sleutel van de feature onvindbaar in
  // de matrix. Niemand kan hem verlenen en niemand ziet dat hij bestaat.
  //
  // Bewust GEEN synoniem van `manage`: een residentie beeindigen is niet "bewonersbeheer",
  // het is het sluiten van een hoofdstuk dat met de hand heropend moet worden.
  "offboard"
];
var PAGE_VERBS = [...PAGE_STANDARD_VERBS, ...PAGE_CUSTOM_VERBS];
var PAGE_REGISTRY = [
  // Public-by-design surface
  { id: "login", label: "Login", route: "/login", file: "packages/portal/src/pages/Login.js", group: "auth", requiredPermissions: [], isPublic: true },
  { id: "health", label: "Health", route: "/health", file: "packages/portal/src/pages/Health.js", group: "auth", requiredPermissions: [], isPublic: true },
  { id: "auth-callback", label: "OAuth callback", route: "/auth-callback", file: "packages/portal/src/pages/AuthCallback.js", group: "auth", requiredPermissions: [], isPublic: true },
  { id: "auth-error", label: "OAuth error", route: "/auth-error", file: "packages/portal/src/pages/AuthError.js", group: "auth", requiredPermissions: [], isPublic: true },
  { id: "permission-denied", label: "Permission denied", route: "/permission-denied", file: "packages/portal/src/pages/PermissionDenied.js", group: "auth", requiredPermissions: [], isPublic: true },
  { id: "alarm-public-uuid", label: "Public alarm view", route: "/alarm/:uuid", file: "packages/portal/src/pages/AlarmDetails/index.jsx", group: "alarms", requiredPermissions: [], isPublic: true, notes: "Token-based public flow used by external responders." },
  { id: "not-found", label: "404", route: "*", file: "packages/portal/src/pages/NotFound.js", group: "auth", requiredPermissions: [], isPublic: true },
  // Gated portal pages
  { id: "dashboard", label: "Dashboard", route: "/dashboard", file: "packages/portal/src/pages/Dashboard.js", group: "dashboard", requiredPermissions: [{ key: "dashboard.view", verb: "read" }] },
  { id: "alarm-details", label: "Alarm details", route: "/alarms/:id", file: "packages/portal/src/pages/AlarmDetails/index.jsx", group: "alarms", requiredPermissions: [{ key: "alarms.view", verb: "read" }, { key: "alarms.edit", verb: "write" }] },
  { id: "alarms-dashboard", label: "Alarms", route: "/alarms", file: "packages/portal/src/pages/AlarmsDashboard.jsx", group: "alarms", requiredPermissions: [{ key: "alarms.view", verb: "read" }, { key: "alarms.create", verb: "write" }, { key: "alarms.delete", verb: "manage" }] },
  { id: "locations", label: "Locations", route: "/locations", file: "packages/portal/src/pages/LocationManagement/index.jsx", group: "locations", requiredPermissions: [
    { key: "locations.view", verb: "read" },
    { key: "locations.edit", verb: "write" },
    { key: "locations.manage", verb: "manage" },
    { key: "locations.create", verb: "create" },
    { key: "locations.delete", verb: "delete" },
    { key: "site_board.manage", verb: "site_board" }
  ], notes: "site_board.manage lives here because corridor panels are provisioned per site from this page." },
  { id: "devices", label: "Devices", route: "/devices", file: "packages/portal/src/pages/DeviceManagement/indexOptimized.jsx", group: "devices", requiredPermissions: [
    { key: "devices.view", verb: "read" },
    { key: "devices.edit", verb: "write" },
    { key: "devices.manage", verb: "manage" },
    { key: "devices.create", verb: "create" },
    { key: "devices.delete", verb: "delete" },
    { key: "devices.admin", verb: "admin" },
    { key: "devices.bulk-action", verb: "bulk" },
    { key: "devices.control_2n", verb: "door" },
    { key: "devices.restart", verb: "restart" },
    { key: "devices.test_call", verb: "test_call" }
  ], notes: "Wrapped in CompanyPermissionRoute (also enforces companyId scope)." },
  { id: "tickets", label: "Tickets", route: "/tickets", file: "packages/portal/src/pages/TicketsManagement/index.jsx", group: "tickets", requiredPermissions: [
    { key: "tickets.view", verb: "read" },
    { key: "tickets.edit", verb: "write" },
    { key: "tickets.manage", verb: "manage" },
    { key: "tickets.create", verb: "create" },
    { key: "tickets.delete", verb: "delete" }
  ], notes: "tickets.create was the trigger for the 2026-08-24 audit: the user role holds it, but only tickets.view was declared here, so it appeared in no column and read as a missing right. tickets.assign/close are deliberately absent - they gate nothing in either repo." },
  { id: "cameras", label: "Camera's", route: "/cameras", file: "packages/portal/src/pages/Cameras/index.jsx", group: "devices", requiredPermissions: [
    { key: "cameras.view", verb: "read" },
    { key: "cameras.edit", verb: "write" },
    { key: "cameras.manage", verb: "manage" },
    { key: "cameras.manage_links", verb: "manage_links" }
  ], notes: "cameras.manage_links is PROTECTED \u2014 die sleutel bepaalt wie er meekijkt en mag daarom nooit via een permissietemplate of een role default verspreid worden. Live beeld staat los van camera_requests.* (opgenomen beeld)." },
  { id: "device-health", label: "Device health", route: "/devices/health", file: "packages/portal/src/pages/DeviceStatusDashboard/index.jsx", group: "devices", requiredPermissions: [{ key: "devices.view", verb: "read" }], notes: "Shares the /devices route wrapper. The company-1 restriction was removed 2026-08-25 (spec 20260825-locations-doorklikken-naar-devices \xA74.6); the gate is now devices.view alone, and tenant isolation is enforced server-side, not by this route." },
  { id: "ticket-quarantine", label: "E-mail quarantine", route: "/tickets/quarantine", file: "packages/portal/src/pages/TicketsManagement/QuarantineQueue.jsx", group: "tickets", requiredPermissions: [{ key: "tickets.view", verb: "read" }], notes: "Inside the tickets.view PermissionRoute in App.js." },
  { id: "tablet-permissions", label: "Tablet permissions", route: "/tablet-permissions", file: "packages/portal/src/pages/TabletPermissions.js", group: "devices", requiredPermissions: [], notes: "Route itself is unguarded; the nav entry carries adminOnly. Listed so the gap is visible rather than absent." },
  { id: "hub-status", label: "Hub status", route: "/hub-status", file: "packages/portal/src/pages/HubStatus.js", group: "monitoring", requiredPermissions: [], notes: "Deliberately unguarded - see the TODO(observability) note in App.js. Gating it would revoke access for current users without monitoring.view." },
  { id: "hub-location-status", label: "Hub status per location", route: "/hub-location-status", file: "packages/portal/src/pages/HubLocationStatus.js", group: "monitoring", requiredPermissions: [], notes: "Same deliberate gap as /hub-status." },
  { id: "tickets-new", label: "Tickets (new layout)", route: "/tickets-new", file: "packages/portal/src/pages/TicketsManagement/index.jsx", group: "tickets", requiredPermissions: [{ key: "tickets.view", verb: "read" }] },
  // ── Pages that existed with no registry entry until 2026-08-24 ────────────
  // Each is routed in App.js, linked from a nav, and gated on the API — but absent
  // here, which meant their keys could never be granted or revoked from the matrix.
  { id: "estimates", label: "Estimates", route: "/estimates", file: "packages/portal/src/pages/Estimates/index.jsx", group: "estimates", requiredPermissions: [
    { key: "estimates.view", verb: "read" },
    { key: "estimates.create", verb: "write" },
    { key: "estimates.offer", verb: "manage" },
    { key: "estimates.bill_without_approval", verb: "bill" }
  ], notes: "Was the worst orphan case: a fully built, route-guarded, nav-linked, API-gated feature with no registry row, so estimates.* was ungrantable by anyone." },
  { id: "qbus-monitor", label: "Qbus monitor", route: "/qbus-monitor", file: "packages/portal/src/pages/QbusMonitor/index.jsx", group: "qbus", requiredPermissions: [{ key: "qbus.view", verb: "read" }], notes: "qbus.view gates 17 API endpoints but gated no page here." },
  { id: "kb-admin", label: "Knowledge base admin", route: "/help/admin", file: "packages/portal/src/pages/KnowledgeBase/KbAdmin.jsx", group: "knowledge_base", requiredPermissions: [{ key: "knowledge_base.manage", verb: "manage" }], notes: "Authoring console. Reading the KB at /help is deliberately ungated; knowledge_base.view stays reserved for the v2 role-scoping path and intentionally gates nothing yet." },
  // `tickets-old` removed 2026-08-24: /tickets-old is not in App.js and pages/Tickets.js
  // is unmounted. It rendered a third matrix row for one surface and invited an admin to
  // grant rights on a page that no longer exists.
  { id: "alarm-flows", label: "Alarm flows", route: "/alarm-flows", file: "packages/portal/src/pages/AlarmFlows/index.jsx", group: "alarm_flows", requiredPermissions: [{ key: "alarm_flows.view", verb: "read" }] },
  { id: "alarm-flow-new", label: "New alarm flow", route: "/alarm-flows/new", file: "packages/portal/src/pages/AlarmFlows/AlarmFlowEditor.jsx", group: "alarm_flows", requiredPermissions: [{ key: "alarm_flows.edit", verb: "write" }] },
  { id: "alarm-flow-edit", label: "Edit alarm flow", route: "/alarm-flows/:id", file: "packages/portal/src/pages/AlarmFlows/AlarmFlowEditor.jsx", group: "alarm_flows", requiredPermissions: [{ key: "alarm_flows.edit", verb: "write" }] },
  { id: "alarm-flow-bindings", label: "Alarm flow bindings", route: "/alarm-flows/bindings", file: "packages/portal/src/pages/AlarmFlows/AlarmFlowBindings.jsx", group: "alarm_flows", requiredPermissions: [{ key: "alarm_flows.manage", verb: "manage" }] },
  { id: "camera-requests", label: "Camera requests", route: "/camera-requests", file: "packages/portal/src/pages/CameraRequests/index.jsx", group: "camera_requests", requiredPermissions: [
    { key: "camera_requests.view", verb: "read" },
    { key: "camera_requests.create", verb: "write" }
  ] },
  { id: "camera-request-detail", label: "Camera request detail", route: "/camera-requests/:id", file: "packages/portal/src/pages/CameraRequests/RequestDetail.jsx", group: "camera_requests", requiredPermissions: [
    { key: "camera_requests.view", verb: "read" },
    { key: "camera_requests.approve_site", verb: "manage" },
    { key: "camera_requests.approve_dpo", verb: "approve_dpo" },
    { key: "camera_requests.retrieve_footage", verb: "footage" }
  ], notes: "camera_requests.approve_dpo is PROTECTED (GDPR) - visible in the matrix, never grantable through a template." },
  { id: "field-interventions", label: "Field interventions", route: "/field-interventions", file: "packages/portal/src/pages/FieldInterventions/index.jsx", group: "field_interventions", requiredPermissions: [
    { key: "field_interventions.view", verb: "read" },
    { key: "field_interventions.view_all", verb: "all" }
  ], notes: "view = own assignments; view_all = every intervention on the site (site-manager scope)." },
  { id: "field-intervention-create", label: "New field intervention", route: "/field-interventions/new", file: "packages/portal/src/pages/FieldInterventions/CreateIntervention.jsx", group: "field_interventions", requiredPermissions: [{ key: "field_interventions.create", verb: "write" }] },
  { id: "field-intervention-detail", label: "Field intervention detail", route: "/field-interventions/:id", file: "packages/portal/src/pages/FieldInterventions/InterventionDetailResponsive.jsx", group: "field_interventions", requiredPermissions: [
    { key: "field_interventions.view", verb: "read" },
    { key: "field_interventions.edit", verb: "write" },
    { key: "estimates.bill_without_approval", verb: "bill" }
  ] },
  // Shop / device catalog. `catalog.view` opens the Shop; `catalog.view_prices`
  // is a second read-type key, so it rides the Custom column (the `read` slot is
  // single-occupancy — verbForKey() takes the first match). See decision 2026-07-17.
  { id: "shop", label: "Shop", route: "/shop", file: "packages/portal/src/pages/Shop/index.jsx", group: "catalog", requiredPermissions: [{ key: "catalog.view", verb: "read" }, { key: "catalog.view_prices", verb: "prices" }], notes: "View-only by construction (no ordering). catalog.view_prices additionally reveals prices; catalog.manage implies it." },
  { id: "catalog-management", label: "Catalog management", route: "/catalog-management", file: "packages/portal/src/pages/Shop/index.jsx", group: "catalog", requiredPermissions: [{ key: "catalog.manage", verb: "manage" }], notes: "Deep-links to the Shop page's catalog-management tab." },
  { id: "users", label: "User management", route: "/users", file: "packages/portal/src/pages/UserManagement/index.jsx", group: "users", requiredPermissions: [
    { key: "users.view", verb: "read" },
    { key: "users.permissions.manage", verb: "manage" },
    { key: "users.manage", verb: "accounts" }
  ], notes: "All THREE keys open the page, and each reaches a different part of it: users.view = the roster read-only, users.manage = create/invite/deactivate/role/PIN, users.permissions.manage = the permission matrix + impersonation. Until 2026-08-26 the nav, the route guard and the page body all named users.permissions.manage alone, so granting users.manage from this very matrix opened nothing (spec 20260826-company-user-management LD-3). users.edit is deliberately absent - it gates nothing in either repo (2026-08-24 audit, class D)." },
  // Permission templates (20260824-permission-templates §9). Renders UserManagement
  // with the Templates tab active — a real route, same pattern as
  // /catalog-management -> Shop, so the nav can highlight it. Registered here
  // registered here for the same reason catalog-management is: PAGE_REGISTRY drives
  // the /users matrix rows, so a key that gates no entry is not grantable in the UI
  // and an admin could never hand it out. `templates.manage` is PROTECTED — no role
  // grants it implicitly, not even admin; it reaches named CARO accounts by migration.
  { id: "permission-templates", label: "Permission templates", route: "/permission-templates", file: "packages/portal/src/pages/UserManagement/index.jsx", group: "users", requiredPermissions: [{ key: "templates.manage", verb: "manage" }, { key: "templates.edit", verb: "write" }], notes: "Deep-links to the User management page's Templates tab. templates.manage = global CARO baselines (PROTECTED, cross-tenant); templates.edit = clone/edit an override for your own company only." },
  { id: "persons", label: "Persons", route: "/persons", file: "packages/portal/src/pages/PersonsManagement/index.jsx", group: "persons", requiredPermissions: [
    { key: "persons.view", verb: "read" },
    { key: "persons.edit", verb: "write" },
    { key: "persons.delete", verb: "manage" },
    { key: "persons.create", verb: "create" },
    { key: "persons.invite", verb: "invite" }
  ] },
  { id: "person-types", label: "Person types", route: "/person-type-management", file: "packages/portal/src/pages/PersonTypeManagement/index.jsx", group: "persons", requiredPermissions: [{ key: "persontypes.manage", verb: "manage" }] },
  { id: "company-management", label: "Company management", route: "/company-management", file: "packages/portal/src/pages/CompanyManagement/index.jsx", group: "system", requiredPermissions: [{ key: "system.admin", verb: "manage" }], notes: "CARO-only, cross-tenant. Spec 20260819-registrationandonboarding/spec-02 LD-CM-SPLIT: the component guard and components/Layout.js must state system.admin ONLY \u2014 both currently also admit company.settings.manage/company.edit, which is how a customer admin reaches a page whose every API call 403s." },
  // Customer-facing counterpart of /company-management (spec-02 D3/D18). Served by
  // /api/company/settings, which is permission-gated per company rather than role-gated,
  // so it works for a company_admin — which /company-management never did.
  { id: "organisation", label: "Organisation", route: "/organisation", file: "packages/portal/src/pages/Organisation/index.jsx", group: "system", requiredPermissions: [{ key: "company.settings.manage", verb: "manage" }, { key: "company.view", verb: "read" }], notes: "The customer edits their OWN organisation here. Entitlements are shown read-only: visible, not grantable." },
  // Customer onboarding — CARO-internal queue (spec-01 §9.3). onboarding.view is
  // PROTECTED and CROSS-TENANT: the queue lists organisations that are not customers yet.
  // Cross-tenant platform hygiene. Lists locations that have NO company across every
  // customer, and assigns one. locations.tenant_repair is PROTECTED for exactly the
  // reason onboarding.view is: the list itself is other customers' data.
  { id: "location-tenancy", label: "Tenant assignment", route: "/admin/location-tenancy", file: "packages/portal/src/pages/Admin/LocationTenancy/index.jsx", group: "locations", requiredPermissions: [{ key: "locations.tenant_repair", verb: "manage" }], notes: "A location with no company has no tenant, so its residents and devices fail closed out of every scoped query. Monitor, not a one-off repair: locations keep being created without a company." },
  { id: "onboarding-queue", label: "Onboarding", route: "/admin/onboarding", file: "packages/portal/src/pages/Admin/Onboarding/index.jsx", group: "system", requiredPermissions: [{ key: "onboarding.view", verb: "read" }, { key: "onboarding.invite", verb: "write" }, { key: "onboarding.approve", verb: "manage" }], notes: "Approving additionally requires onboarding.approve. Both keys are in PROTECTED_PERMISSIONS \u2014 no role grants them implicitly, not even admin. Menu entry lives in components/Layout.js, NOT here." },
  // The three public onboarding routes are registered for documentation only. They render
  // OUTSIDE the authenticated shell (App.js public block) and have no permission gate —
  // the invite / status / activation token is the entire authorisation.
  { id: "onboarding-form", label: "Onboarding form (public)", route: "/onboarding", file: "packages/portal/src/pages/Onboarding/index.jsx", group: "system", requiredPermissions: [], isPublic: true, notes: "PUBLIC, invite-token gated (D11). No account exists at this point \u2014 the form creates an application, not a user." },
  { id: "onboarding-status", label: "Onboarding status (public)", route: "/onboarding/status/:token", file: "packages/portal/src/pages/Onboarding/Status.jsx", group: "system", requiredPermissions: [], isPublic: true, notes: "PUBLIC. Keyed by a hashed token, never by the guessable ONB- reference (D19)." },
  { id: "onboarding-activate", label: "Activate account (public)", route: "/activate/:token", file: "packages/portal/src/pages/Onboarding/Activate.jsx", group: "system", requiredPermissions: [], isPublic: true, notes: "PUBLIC. First password set for the company_admin created at approval. 7-day single-use token." },
  { id: "service-management", label: "Service management", route: "/service-management", file: "packages/portal/src/pages/ServiceManagement/index.jsx", group: "services", requiredPermissions: [{ key: "services.manage", verb: "manage" }, { key: "services.view", verb: "read" }] },
  { id: "device-type-management", label: "Device type management", route: "/device-type-management", file: "packages/portal/src/pages/DeviceTypeManagement/index.jsx", group: "device_types", requiredPermissions: [{ key: "device_types.manage", verb: "manage" }, { key: "device_types.view", verb: "read" }] },
  { id: "device-linking", label: "Device linking", route: "/device-linking", file: "packages/portal/src/pages/DeviceLinking/index.jsx", group: "device_linking", requiredPermissions: [{ key: "device_linking.manage", verb: "manage" }] },
  { id: "baseip-management", label: "BaseIP", route: "/baseip-management", file: "packages/portal/src/pages/BaseIPManagement/index.jsx", group: "baseip", requiredPermissions: [{ key: "baseip.manage", verb: "manage" }] },
  { id: "enocean-buttons", label: "EnOcean buttons", route: "/enocean-buttons", file: "packages/portal/src/pages/EnOceanButtonManagement/index.jsx", group: "enocean", requiredPermissions: [{ key: "enocean.view", verb: "read" }, { key: "enocean.edit", verb: "write" }] },
  { id: "enocean-import", label: "EnOcean import", route: "/enocean-import", file: "packages/portal/src/pages/EnOceanButtonImport/index.jsx", group: "enocean", requiredPermissions: [{ key: "enocean.import", verb: "manage" }] },
  { id: "location-data-import", label: "Location data import", route: "/location-data-import", file: "packages/portal/src/pages/LocationDataImport/index.jsx", group: "locations", requiredPermissions: [{ key: "locations.manage", verb: "manage" }] },
  { id: "location-phone-numbers", label: "Location phone numbers", route: "/location-phone-numbers", file: "packages/portal/src/pages/LocationPhoneNumbers/index.jsx", group: "locations", requiredPermissions: [{ key: "locations.phone_numbers", verb: "manage" }], notes: "Verifies locations.data.phoneNumber (what {{loc.data.phoneNumber}} dials in every alarm flow) against sip_endpoints and the Clearvox number, per site, and corrects it. One key for read and write: the overview alone reveals the number of every apartment." },
  // `group: 'system'`, niet een eigen 'company'-groep. Een paginagroep die in geen enkel
  // gebied staat is in de permissiematrix ONVINDBAAR -- dezelfde klasse fout als de twee
  // onverleenbare firmwaresleutels -- en `permissionAreas.test.ts` faalt erop. Huisstijl
  // hoort bij /organisation, dat op dezelfde groep staat: de klant bewerkt hier zijn EIGEN
  // organisatie.
  { id: "branding", label: "Huisstijl", route: "/branding", file: "packages/portal/src/pages/Branding/index.jsx", group: "system", requiredPermissions: [{ key: "branding.manage", verb: "manage" }], notes: "Logo, kleuren, titel en lettertype van de organisatie of van een van haar gebouwen. Resolutie is PER VELD: een gebouw dat alleen een logo zet, houdt de kleuren van de organisatie. Aan geen enkele rol standaard toegekend \u2014 het bepaalt wat de kassa en 800+ bewonerstablets tonen." },
  { id: "candidate-cleanup", label: "Kandidaten opruimen", route: "/candidate-cleanup", file: "packages/portal/src/pages/CandidateCleanup/index.jsx", group: "persons", requiredPermissions: [{ key: "persons.view", verb: "read" }], notes: "Kandidaatdossiers die blijven hangen, met hun ouderdom en het aantal medische antwoorden. Het scherm STELT VOOR; wissen zelf vraagt intake.medical_read, want wie beslist dat zorgdata weg mag, hoort tot die gegevens gerechtigd te zijn geweest. De lijst telt de antwoorden, ze toont ze niet." },
  { id: "print-profiles", label: "Afdrukken naar derden", route: "/print-profiles", file: "packages/portal/src/pages/PrintProfiles/index.jsx", group: "persons", requiredPermissions: [{ key: "intake.templates", verb: "manage" }], notes: "Welke velden meegaan naar WGK, huisarts of mutualiteit, en naar welk adres. TWEE sloten: het veld moet deelbaar zijn (bij het formulier) EN dit profiel moet het bij naam noemen. Een niet-deelbaar veld staat hier uitgegrijsd MET de reden -- verbergen zou de vraag alleen verplaatsen." },
  { id: "intake-forms", label: "Intakeformulieren", route: "/intake-forms", file: "packages/portal/src/pages/IntakeForms/index.jsx", group: "persons", requiredPermissions: [{ key: "intake.templates", verb: "manage" }], notes: "De vragenlijst samenstellen. Een gepubliceerde versie is bevroren; lopende gesprekken houden de versie waarmee ze begonnen. Publiceren zit achter een EIGEN sleutel (intake.publish): bewerken raakt niets, publiceren bepaalt wat elk volgend gesprek vraagt." },
  { id: "intakes", label: "Intakegesprekken", route: "/intake", file: "packages/portal/src/pages/Intakes/index.jsx", group: "persons", requiredPermissions: [
    { key: "intake.view", verb: "read" },
    // `write` en niet `conduct`: de rij voor /intake/:uuid/conduct hiernaast doet het al
    // zo, en twee verbnamen voor dezelfde sleutel maken de matrix onleesbaar.
    { key: "intake.conduct", verb: "write" }
  ], notes: "De lijst van gevoerde en lopende gesprekken, en de enige plek waar er een START. Draagt geen antwoorden: alleen wie, welk formulier, welke toestand en wanneer -- wat er IN een gesprek staat beslist viewIntake, die de medische afscherming kent. intake.conduct is apart omdat lezen en voeren twee dingen zijn." },
  { id: "intake-conduct", label: "Intakegesprek", route: "/intake/:uuid/conduct", file: "packages/portal/src/pages/IntakeConduct/index.jsx", group: "persons", requiredPermissions: [{ key: "intake.conduct", verb: "write" }], notes: "Het gesprek live met de bewoner. Doorlopend opslaan; bewonersweergave verbergt medische en interne velden. Medische antwoorden komen al afgeschermd van de server -- het portaal filtert niets zelf." },
  { id: "resident-onboarding", label: "Bewoner laten intrekken", route: "/persons/onboard", file: "packages/portal/src/pages/ResidentOnboarding/index.jsx", group: "persons", requiredPermissions: [{ key: "persons.create", verb: "create" }], notes: 'De intredewizard. Rendert de GESNAPSHOTTE checklist van de klant (spec-02), niet een vaste lijst van negen -- zo bepaalt het sjabloon wat er in beeld staat en houdt een lopende intrede de lijst waarmee ze begon. Een stap met een uitgeschakelde module wordt GETOOND en niet verborgen: anders is "dat hebben wij niet" niet te onderscheiden van "de flow is het vergeten".' },
  { id: "resident-offboarding", label: "Bewoner laten uittrekken", route: "/persons/offboard", file: "packages/portal/src/pages/ResidentOffboarding/index.jsx", group: "persons", requiredPermissions: [{ key: "residents.offboard", verb: "offboard" }], notes: "De uittredewizard, spiegel van /persons/onboard. EIGEN sleutel en niet persons.edit: afsluiten ontkoppelt facturatie, trekt de tablet in en sluit een hoofdstuk dat alleen met de hand heropend kan worden. Stappen 2, 3 en 4 (plaatsbeschrijving, meterstanden, afrekening) blokkeren het afsluiten NOOIT -- ze blijven op de openstaande lijst staan. Stap 5 en 6 (toegang, tablet) wel: 6 en 7 lopen zelfs in een transactie, want tussen die twee zou een automatische aanmelding de tablet aan de vertrekkende bewoner herbinden." },
  { id: "checklist-templates", label: "Intrede-checklist", route: "/checklist-templates", file: "packages/portal/src/pages/ChecklistTemplates/index.jsx", group: "persons", requiredPermissions: [{ key: "resident_onboarding.templates", verb: "manage" }], notes: "Fases en taken van de bewonersintrede, per organisatie of per gebouw. EEN bewerking raakt elke TOEKOMSTIGE intrede; lopende dossiers houden de kopie die ze bij de start meekregen. NIET de onboarding.*-namespace, die gaat over klantregistratie en is CARO-only." },
  { id: "unit-audit", label: "Unit audit", route: "/unit-audit", file: "packages/portal/src/pages/UnitAudit/index.jsx", group: "unit_audit", requiredPermissions: [{ key: "unit_audit.view", verb: "read" }, { key: "unit_audit.manage", verb: "manage" }], notes: "unit_audit.manage edits the organisation-wide profile (which device types every apartment must contain). Own organisation only; platform admins may edit any. Granted to nobody by default." },
  { id: "qbus-prefixes", label: "Domotica naamclassificatie", route: "/qbus-prefixes", file: "packages/portal/src/pages/QbusPrefixes/index.jsx", group: "qbus", requiredPermissions: [{ key: "qbus.view", verb: "read" }], notes: "Bepaalt wat een bewoner op de tablet ziet. Lezen op qbus.view; regels wijzigen en herseeden vraagt qbus.manage in de route zelf." },
  { id: "qbus-output-types", label: "Qbus output types", route: "/qbus-output-types", file: "packages/portal/src/pages/QbusOutputTypeManagement/index.jsx", group: "qbus", requiredPermissions: [{ key: "qbus.manage", verb: "manage" }] },
  { id: "automations", label: "Automations", route: "/automations", file: "packages/portal/src/pages/Automations/index.jsx", group: "automations", requiredPermissions: [
    { key: "automations.view", verb: "read" },
    { key: "automations.edit", verb: "write" },
    { key: "automations.manage", verb: "manage" }
  ] },
  { id: "qbus-friendly-names", label: "Qbus friendly names", route: "/qbus-friendly-names", file: "packages/portal/src/pages/QbusFriendlyNames/index.jsx", group: "qbus", requiredPermissions: [{ key: "qbus.edit", verb: "write" }] },
  // 20260831-domotica-meldingen-bewonerstablet §11.2. Hier geregistreerd in FASE 1, terwijl
  // de portaalpagina's zelf pas in fase 2/3 landen — anders is er een periode waarin de
  // sleutels bestaan maar in de matrix onvindbaar zijn, en dan kan niemand ze verlenen. De
  // registratie is wat een sleutel GRANTBAAR maakt; de sidebar toont een pagina pas als er
  // een navigatie-item naar wijst, en dat komt met de pagina mee.
  { id: "resident-notifications", label: "Bewonersmeldingen", route: "/resident-notifications", file: "packages/portal/src/pages/ResidentNotifications/index.jsx", group: "qbus", requiredPermissions: [
    { key: "resident_notifications.view", verb: "read" },
    { key: "resident_notifications.edit", verb: "write" },
    { key: "resident_notifications.manage", verb: "manage" },
    { key: "resident_notifications.send", verb: "send" }
  ], notes: "resident_notifications.manage is PROTECTED \u2014 die sleutel bepaalt welke domotica-output een bewoner met \xE9\xE9n druk kan schakelen, en mag daarom nooit via een role default of een permissietemplate verspreid worden. `.send` is een eigen kolom: een melding op iemands scherm zetten is iets anders dan hem schrijven. De portaalpagina zelf komt in fase 2." },
  { id: "notification-sounds", label: "Meldingsgeluiden", route: "/notification-sounds", file: "packages/portal/src/pages/NotificationSounds/index.jsx", group: "qbus", requiredPermissions: [
    { key: "resident_notifications.view", verb: "read" },
    { key: "resident_notifications.edit", verb: "write" }
  ], notes: "De geluidbibliotheek. Fase 2 start CARO-only (alleen platform-brede, ingebouwde geluiden); klantupload volgt na een uploadvalidatieronde, want een tablet die een verkeerd geluid speelt is een supportincident." },
  { id: "telephony", label: "Telephony", route: "/telephony", file: "packages/portal/src/pages/TelephonyManagement/index.jsx", group: "telephony", requiredPermissions: [{ key: "telephony.view", verb: "read" }, { key: "telephony.edit", verb: "write" }, { key: "telephony.manage", verb: "manage" }] },
  { id: "eink-designer", label: "E-ink designer", route: "/eink-designer", file: "packages/portal/src/pages/EinkDesigner/index.jsx", group: "eink", requiredPermissions: [{ key: "eink.manage", verb: "manage" }] },
  { id: "integration-management", label: "Integrations", route: "/integration-management", file: "packages/portal/src/pages/IntegrationManagement.jsx", group: "integrations", requiredPermissions: [{ key: "integrations.manage", verb: "manage" }] },
  { id: "emergency-services-admin", label: "Emergency services", route: "/admin/integrations/emergency-services", file: "packages/portal/src/pages/EmergencyServicesManagement.jsx", group: "emergency_services", requiredPermissions: [{ key: "emergency_services.manage", verb: "manage" }] },
  { id: "twon-commanders-admin", label: "2N commanders", route: "/admin/integrations/twon/commanders", file: "packages/portal/src/components/Integrations/TwoN/CommanderList.jsx", group: "integrations", requiredPermissions: [{ key: "integrations.manage", verb: "manage" }] },
  { id: "firmware-management", label: "Firmware management", route: "/firmware-management", file: "packages/portal/src/pages/FirmwareManagement.jsx", group: "firmware", requiredPermissions: [
    // All four declared `write` until 2026-08-24. verbForKey() takes the FIRST match,
    // so only firmware.create rendered — firmware.edit and firmware.delete were
    // silently invisible and ungrantable. Spread across distinct slots now.
    { key: "firmware.view", verb: "read" },
    { key: "firmware.edit", verb: "write" },
    { key: "firmware.delete", verb: "manage" },
    { key: "firmware.create", verb: "create" }
  ] },
  { id: "notification-management", label: "Notification management", route: "/notification-management", file: "packages/portal/src/pages/NotificationManagement.js", group: "notifications", requiredPermissions: [{ key: "notifications.manage", verb: "manage" }] },
  // Audit trail / NIS2 reporting (2026-08-25). Both keys are PROTECTED: reading the
  // trail shows which named member of staff opened which resident record.
  { id: "audit", label: "Audit trail", route: "/audit", file: "packages/portal/src/pages/AuditTrail/index.jsx", group: "audit", requiredPermissions: [
    { key: "audit.view", verb: "read" },
    { key: "audit.export", verb: "export" }
  ], notes: "NIS2 reporting: sign-ins, refused sign-ins, reads of personal data, and changes. Tenant-scoped like everything else - a customer sees only their own staff." },
  { id: "settings", label: "Settings", route: "/settings", file: "packages/portal/src/pages/Settings.js", group: "settings", requiredPermissions: [{ key: "settings.view", verb: "read" }] },
  { id: "notifications", label: "Notifications", route: "/notifications", file: "packages/portal/src/pages/Notifications.js", group: "notifications", requiredPermissions: [{ key: "notifications.view", verb: "read" }] },
  { id: "monitoring", label: "Monitoring", route: "/monitoring", file: "packages/portal/src/pages/MonitoringDashboard/index.jsx", group: "monitoring", requiredPermissions: [{ key: "monitoring.view", verb: "read" }] },
  // Observability feedback loop (20260807-observability-feedback-loop §7).
  //
  // A separate page rather than a tab on /monitoring, which the spec originally proposed.
  // /monitoring is a fleet dashboard about devices being reachable; this is about defects in
  // our own code being real or remedied, and folding it in would bury the only surface that
  // shows whether a fix worked. Same `monitoring.view` gate, own entry — `read` is
  // single-occupancy per page.
  //
  // The write on this page (marking a fix as shipped) is gated in the component on
  // `monitoring.manage`, held by `developer` only; the server enforces it with a 403.
  { id: "observability", label: "Observability", route: "/observability", file: "packages/portal/src/pages/Observability/index.jsx", group: "monitoring", requiredPermissions: [{ key: "monitoring.view", verb: "read" }, { key: "monitoring.manage", verb: "manage" }] },
  // Troubleshooting recipes (20260805-api-observability §4.10). Same monitoring.view gate;
  // the shell commands inside it are gated separately on monitoring.manage in the component,
  // because they name internal hosts and are only actionable with SSH anyway.
  { id: "troubleshooting", label: "Troubleshooting", route: "/troubleshooting", file: "packages/portal/src/pages/Troubleshooting/index.jsx", group: "monitoring", requiredPermissions: [{ key: "monitoring.view", verb: "read" }] },
  { id: "help", label: "Help", route: "/help", file: "packages/portal/src/pages/Help.js", group: "auth", requiredPermissions: [], notes: "Authenticated-only; no permission key." },
  { id: "release-notes", label: "Release notes", route: "/release-notes/:version", file: "packages/portal/src/pages/ReleaseNotesViewer.js", group: "auth", requiredPermissions: [], notes: "Authenticated-only; no permission key." },
  { id: "developer-info", label: "Developer info", route: "/developer-info", file: "packages/portal/src/pages/DeveloperInfo.js", group: "system", requiredPermissions: [{ key: "system.admin", verb: "manage" }] },
  { id: "indoor-mapping", label: "Indoor mapping", route: "/indoor-mapping", file: "packages/portal/src/pages/IndoorMapping/index.jsx", group: "indoor", requiredPermissions: [{ key: "indoor.manage", verb: "manage" }] },
  { id: "indoor-navigation-preview", label: "Indoor navigation preview", route: "/indoor-navigation-preview", file: "packages/portal/src/pages/IndoorNavigationPreview/index.jsx", group: "indoor", requiredPermissions: [{ key: "indoor.view", verb: "read" }] },
  { id: "business-update", label: "Business update", route: "/business-update", file: "packages/portal/src/pages/BusinessUpdate/index.jsx", group: "system", requiredPermissions: [{ key: "system.admin", verb: "manage" }] },
  { id: "sales-presentation", label: "Sales presentation", route: "/sales-presentation", file: "packages/portal/src/pages/SalesPresentation/index.jsx", group: "system", requiredPermissions: [{ key: "system.admin", verb: "manage" }] },
  // Portal Ideabox (spec 20260817-portal-ideabox §8).
  // ⚠ ONE entry, one `read` verb. `read` is single-occupancy per page, so the CARO
  // Triage tab does NOT get a second `read` row here — it is a tab inside this page
  // and it gates itself on `ideas.triage`, which is a PROTECTED permission.
  { id: "ideas", label: "Ideas", route: "/ideas", file: "packages/portal/src/pages/Ideas/index.jsx", group: "ideas", requiredPermissions: [
    { key: "ideas.view", verb: "read" },
    { key: "ideas.create", verb: "write" },
    { key: "ideas.manage", verb: "manage" },
    { key: "ideas.vote", verb: "vote" },
    { key: "ideas.triage", verb: "triage" }
  ], notes: "ideas.triage and ideas.manage are PROTECTED - declared so they are visible in the matrix, but a template can never carry them." },
  // -- Agenda (spec 20260825-animatie-activiteiten-en-agenda, LD-1) -----------
  //
  // ⚠ NO `module` FIELD, on purpose. The agenda is the BASE layer: three tiers of
  // plain calendar entries, no signups and no money. Signups and prices live on
  // /activities, which IS module-gated. Three gates over three different surfaces:
  //
  //   tablet agenda visible   ← building feature flag `agenda` (default TRUE) — untouched
  //     └─ activities+signup  ← module `activities` (per company, default OFF)
  //          └─ with a price  ← + module `invoicing` AND a unit billing account
  //
  // Gating this page behind the paid module instead would have been the obvious
  // shortcut and a fleet-wide regression: `featureDefaults.ts` has `agenda: true`,
  // so every existing customer's tablets would have lost their agenda on release day
  // (spec F-2).
  //
  // Resident-PRIVATE entries are gated by NEITHER key here (LD-9) — they are stripped
  // server-side for everyone who is not the owner or in CROSS_ORG_ROLES.
  { id: "agenda", label: "Agenda", route: "/agenda", file: "packages/portal/src/pages/Agenda/index.jsx", group: "agenda", requiredPermissions: [
    { key: "agenda.view", verb: "read" },
    { key: "agenda.manage", verb: "manage" }
  ], notes: "BASE, not a paid module. Private resident entries are stripped SERVER-side and no permission key grants them." },
  // -- Activities (spec 20260825-animatie-activiteiten-en-agenda §6). module: 'activities'
  //
  // The PAID half. /agenda above is BASE and deliberately carries no `module` field:
  // gating the base agenda behind this key would have removed the agenda from every
  // tablet in the field on release day (spec F-2).
  //
  // moduleMode 'read' on the catalog: an occurrence roster is an attendance record and
  // a priced signup is an accounting fact, so a customer who stops paying keeps the
  // list and loses every create and edit (D27). The settings page is write-only
  // territory and gets the default.
  { id: "activities", label: "Activities", route: "/activities", file: "packages/portal/src/pages/Activities/index.jsx", group: "activities", requiredPermissions: [
    { key: "activities.view", verb: "read" },
    { key: "activities.manage", verb: "manage" },
    { key: "activities.roster", verb: "roster" },
    { key: "activities.enroll", verb: "enroll" },
    { key: "activities.pricing", verb: "pricing" }
  ], module: "activities", moduleMode: "read", notes: "activities.pricing is required IN ADDITION TO activities.manage to set a price - it decides what lands on an apartment bill. activities.roster is separate because participation in a named activity is personal data." },
  { id: "activities-settings", label: "Activity settings", route: "/activities/settings", file: "packages/portal/src/pages/Activities/Settings.jsx", group: "activities", requiredPermissions: [
    { key: "activities.view", verb: "read" },
    { key: "activities.manage", verb: "manage" }
  ], module: "activities", notes: "Per-site coordinator e-mail, default signup window, timezone. An empty coordinator e-mail blocks saving an activity that has a capacity (LD-8)." },
  // -- Plaatsbeschrijvingen (20260901-bewoner-onboarding/plaatsbeschrijving.md §8) --
  //
  // Routes are English like every other portal route (/persons, /devices); the LABEL is
  // Dutch because a human reads it. Every entry carries module: 'inspections' -- without
  // it the sidebar cannot hide the page for a customer who has not bought the module, and
  // the route guard is not auditable against the registry.
  //
  // moduleMode 'read' on the archive and the detail: a published condition report is
  // evidence with a retention obligation behind it, so a customer who stops paying keeps
  // every report they made and can still export it -- exactly as they keep their invoices.
  // Capture and templates have no such argument and get the default (write-gated).
  { id: "inspections", label: "Plaatsbeschrijvingen", route: "/inspections", file: "packages/portal/src/pages/Inspections/index.jsx", group: "inspections", requiredPermissions: [
    { key: "inspections.view", verb: "read" },
    { key: "inspections.export", verb: "export" }
  ], module: "inspections", moduleMode: "read", notes: "The archive, with the filters from the functional spec \xA78 (per flat, period, moment, status). inspections.export is separate from view because an export leaves our retention and access controls behind." },
  { id: "inspection-detail", label: "Plaatsbeschrijving", route: "/inspections/:uuid", file: "packages/portal/src/pages/Inspections/Detail.jsx", group: "inspections", requiredPermissions: [
    { key: "inspections.view", verb: "read" },
    { key: "inspections.publish", verb: "publish" }
  ], module: "inspections", moduleMode: "read", notes: "Detail, the intake/departure comparison, and the three publication routes. Publishing FREEZES the report permanently, which is why inspections.publish is not implied by inspections.perform. Every read writes an inspection_viewed audit row - the report contains photographs of a home." },
  { id: "inspection-capture", label: "Plaatsbeschrijving opnemen", route: "/inspections/:uuid/capture", file: "packages/portal/src/pages/Inspections/Capture.jsx", group: "inspections", requiredPermissions: [
    { key: "inspections.perform", verb: "perform" }
  ], module: "inspections", notes: "The field surface. ONE React route published twice: here for a PC or phone, and inside packages/inspection-app (a Capacitor shell modelled on pos-app) for a field tablet. NOT in the resident app - that fleet is 800+ kiosk-locked apartment devices and this surface caches photographs of those apartments." },
  { id: "inspection-templates", label: "Plaatsbeschrijving-sjablonen", route: "/inspections/templates", file: "packages/portal/src/pages/Inspections/Templates.jsx", group: "inspections", requiredPermissions: [
    { key: "inspections.templates", verb: "templates" }
  ], module: "inspections", notes: "Room and checklist templates, company-scoped. One edit changes every FUTURE report; existing reports are unaffected because they snapshot their template at creation (LD-4)." },
  // -- Documentclassificatie (specs/20260902-documentclassificatie, spec §6/§7, plan §6, PD-9) --
  //
  // Vier registerrijen, en dit is wat de sleutels TOEKENBAAR maakt (CLAUDE.md stap 3): een sleutel
  // die geen registerpagina bewaakt kan in de matrix aan niemand worden gegeven, en de zijbalk
  // verbergt een pad dat het register niet kent nooit. Geen van de tien sleutels staat in een
  // role default of template; de drie beheerpagina's dragen elk een PROTECTED sleutel.
  //
  // `/documents` heeft GEEN read-verb, met opzet: een bestand lezen is het antwoord van de
  // engine, nooit een sleutel (D-14). De zes topic-sleutels zijn de matrixvorm van een
  // topic-grant en `documents.classify` — als eerste, zodat visibilityPermissionForRoute() hem
  // kiest — is wat de werklijst op deze pagina zinvol maakt.
  { id: "documents", label: "Documenten", route: "/documents", file: "packages/portal/src/pages/Documents/index.jsx", group: "documents", requiredPermissions: [
    { key: "documents.classify", verb: "classify" },
    { key: "documents.read.medical", verb: "medical" },
    { key: "documents.read.financial", verb: "financial" },
    { key: "documents.read.legal", verb: "legal" },
    { key: "documents.read.technical", verb: "technical" },
    { key: "documents.read.housing", verb: "housing" },
    { key: "documents.read.hr", verb: "hr" }
  ], notes: "Landingspagina en classificatie-werklijst (PD-9). Geen read-verb: lezen is de beslissing van documentAccessEngine, niet een sleutel. De zes topic-sleutels schrijven bij het stempelen een document_topic_grants-rij (LD-5); documents.classify bewaakt verlagen, topics wijzigen en verplaatsen naar een losser pad (LD-8)." },
  { id: "documents-grants", label: "Documenttoegang \u2014 grants", route: "/documents/grants", file: "packages/portal/src/pages/Documents/Grants.jsx", group: "documents", requiredPermissions: [
    { key: "documents.access_admin", verb: "access_admin" }
  ], notes: "PROTECTED. Handmatige grants, strikt-grants met getypte bevestiging, 72-uurs noodgrants, recertificatie. Onverenigbaar met documents.exclusion_admin (P6/E1) \u2014 de API weigert het paar met 422." },
  { id: "documents-exclusions", label: "Documenttoegang \u2014 uitsluitingen", route: "/documents/exclusions", file: "packages/portal/src/pages/Documents/Exclusions.jsx", group: "documents", requiredPermissions: [
    { key: "documents.exclusion_admin", verb: "exclusion_admin" }
  ], notes: "PROTECTED. Exclusions per gebruiker op een dossier, een boom of een topic; reden \u2265 20 tekens, maximaal 12 maanden, nooit fysiek verwijderd. Onverenigbaar met documents.access_admin." },
  { id: "documents-archive", label: "Archivering", route: "/documents/archive", file: "packages/portal/src/pages/Documents/Archive.jsx", group: "documents", requiredPermissions: [
    { key: "documents.retention", verb: "retention" }
  ], notes: "PROTECTED. De bewaarvoorstellen per klant (spec \xA79), legal hold, en de geauditeerde batch-verwijdering met getypt aantal boven 100. Niets wordt automatisch verwijderd (D-17)." },
  // -- Paid module administration (spec 20260818-invoicingsystem, spec-00 §6) --
  // These two are deliberately NOT module-gated: the admin screen is how a module
  // gets enabled in the first place, and the settings screen must be able to say
  // "your organisation does not have this".
  { id: "modules-admin", label: "Modules (CARO)", route: "/admin/modules", file: "packages/portal/src/pages/Admin/Modules/index.jsx", group: "system", requiredPermissions: [{ key: "modules.manage", verb: "manage" }], notes: "CARO-only. modules.manage is PROTECTED - company_admin does not inherit it." },
  { id: "modules-settings", label: "My modules", route: "/settings/modules", file: "packages/portal/src/pages/Settings/Modules.jsx", group: "settings", requiredPermissions: [{ key: "modules.view", verb: "read" }] },
  // -- Resident invoicing (spec-01 §7). module: 'invoicing' -------------------
  // moduleMode 'read' on the list/detail/export surfaces: after a disable the
  // customer keeps access to invoices they are legally required to retain (D27).
  { id: "invoicing", label: "Invoicing", route: "/invoicing", file: "packages/portal/src/pages/Invoicing/index.jsx", group: "invoicing", requiredPermissions: [{ key: "invoicing.view", verb: "read" }, { key: "invoicing.view_amounts", verb: "amounts" }, { key: "invoicing.export", verb: "manage" }], module: "invoicing", moduleMode: "read", notes: "invoicing.view_amounts is PROTECTED; amounts are stripped SERVER-side, the column here is only an affordance." },
  { id: "invoicing-items", label: "Billable items", route: "/invoicing/items", file: "packages/portal/src/pages/Invoicing/Items.jsx", group: "invoicing", requiredPermissions: [{ key: "invoicing.view", verb: "read" }, { key: "invoicing.manage", verb: "write" }], module: "invoicing", moduleMode: "read" },
  { id: "invoicing-detail", label: "Invoice detail", route: "/invoicing/invoices/:uuid", file: "packages/portal/src/pages/Invoicing/InvoiceDetail.jsx", group: "invoicing", requiredPermissions: [{ key: "invoicing.view", verb: "read" }, { key: "invoicing.manage", verb: "write" }, { key: "invoicing.issue", verb: "manage" }], module: "invoicing", moduleMode: "read" },
  { id: "invoicing-payments", label: "Payments", route: "/invoicing/payments", file: "packages/portal/src/pages/Invoicing/Payments.jsx", group: "invoicing", requiredPermissions: [{ key: "invoicing.view", verb: "read" }, { key: "invoicing.payments", verb: "manage" }], module: "invoicing", moduleMode: "read" },
  { id: "invoicing-config", label: "Invoicing configuration", route: "/invoicing/config", file: "packages/portal/src/pages/Invoicing/Config.jsx", group: "invoicing", requiredPermissions: [{ key: "invoicing.view", verb: "read" }, { key: "invoicing.config", verb: "manage" }], module: "invoicing" },
  // -- Cafeteria POS (spec-02 §7). module: 'pos' ------------------------------
  { id: "pos-catalog", label: "Cafeteria catalog", route: "/pos/catalog", file: "packages/portal/src/pages/Pos/Catalog.jsx", group: "pos", requiredPermissions: [{ key: "pos.view", verb: "read" }, { key: "pos.catalog", verb: "manage" }], module: "pos" },
  { id: "pos-config", label: "Till configuration", route: "/pos/config", file: "packages/portal/src/pages/Pos/Config.jsx", group: "pos", requiredPermissions: [{ key: "pos.view", verb: "read" }, { key: "pos.catalog", verb: "manage" }], module: "pos" },
  { id: "pos-restrictions", label: "Product restrictions", route: "/pos/restrictions", file: "packages/portal/src/pages/Pos/Restrictions.jsx", group: "pos", requiredPermissions: [{ key: "pos.view", verb: "read" }, { key: "pos.restrictions", verb: "manage" }], module: "pos" },
  { id: "pos-reports", label: "Cafeteria reports", route: "/pos/reports", file: "packages/portal/src/pages/Pos/Reports.jsx", group: "pos", requiredPermissions: [{ key: "pos.reports", verb: "read" }, { key: "pos.report_persons", verb: "persons" }, { key: "pos.export", verb: "manage" }], module: "pos", moduleMode: "read", notes: "pos.report_persons is PROTECTED - itemised consumption per named resident. Stripped server-side." },
  { id: "pos-day-closes", label: "Day closes & cash", route: "/pos/day-closes", file: "packages/portal/src/pages/Pos/DayCloses.jsx", group: "pos", requiredPermissions: [{ key: "pos.view", verb: "read" }, { key: "pos.cash_count", verb: "manage" }], module: "pos", moduleMode: "read" },
  { id: "pos-terminals", label: "Till terminals", route: "/pos/terminals", file: "packages/portal/src/pages/Pos/Terminals.jsx", group: "pos", requiredPermissions: [{ key: "pos.view", verb: "read" }, { key: "pos.terminals", verb: "manage" }], module: "pos" }
];
function getPageById(id) {
  return PAGE_REGISTRY.find((p) => p.id === id);
}
function getPagesByGroup(group) {
  return PAGE_REGISTRY.filter((p) => p.group === group);
}
function getPageByRoute(route) {
  return PAGE_REGISTRY.find((p) => p.route === route);
}
function visibilityPermissionForRoute(route) {
  const page = getPageByRoute(route);
  if (!page || page.isPublic || page.requiredPermissions.length === 0) return null;
  const read = page.requiredPermissions.find((r) => r.verb === "read");
  return (read ?? page.requiredPermissions[0]).key;
}
function isUngatedPage(entry) {
  return !entry.isPublic && entry.requiredPermissions.length === 0;
}
function getUngatedPages() {
  return PAGE_REGISTRY.filter(isUngatedPage);
}

// src/constants/permissionAreas.ts
var PERMISSION_AREAS = [
  {
    id: "care",
    label: "Care & alarms",
    description: "Everything that happens when a resident needs someone: alarms, how they escalate, and who is called.",
    groups: ["alarms", "alarm_flows", "emergency_services"]
  },
  {
    id: "work",
    label: "Tickets & interventions",
    description: "The work itself \u2014 reported, planned, carried out and quoted. Estimates live here rather than with the Shop because they are raised from inside a ticket.",
    groups: ["tickets", "field_interventions", "estimates"]
  },
  {
    id: "residents",
    label: "Residents & locations",
    description: "Who lives where, and the records about them. Camera footage requests sit here because the subject of a request is a resident.",
    groups: [
      "persons",
      "locations",
      "camera_requests",
      "unit_audit",
      // 20260825-animatie-activiteiten-en-agenda. Both live with RESIDENTS rather than
      // with the paid modules next door, and the partition rule forces the choice:
      //
      //   `agenda` is BASE — three tiers of plain calendar entries, no money — so
      //   filing it under "Billing & till" would be wrong on the face of it.
      //
      //   `activities` IS a paid module, and 'commerce' is where the other two live.
      //   But an area is what an ADMIN THINKS IN, and the person who runs animation is
      //   the person who runs the home, not the person who runs the books. Splitting
      //   agenda from activities across two areas would separate the two halves of one
      //   feature — you cannot grant "our animator can plan things" in one action.
      //
      // The commercial gate is unaffected: `activities` stays in PAID_MODULES and the
      // module check is independent of which area its pages are grouped under.
      "agenda",
      "activities",
      // 20260901-bewoner-onboarding/plaatsbeschrijving.md. Same reasoning as `activities`
      // above, and the partition rule forces the same answer: `inspections` IS a paid
      // module and the other paid modules live in 'commerce', but an area is what an ADMIN
      // THINKS IN, and a plaatsbeschrijving is a record about a resident's home — it sits
      // beside `persons` and `locations`, not beside the till.
      //
      // A damage finding can eventually reach a bill, but so can an activity, and filing
      // either under "Billing & till" would separate the people who DO the work from the
      // permission that lets them. The commercial gate is unaffected: `inspections` stays
      // in PAID_MODULES and the module check is independent of the area.
      "inspections",
      // 20260902-documentclassificatie. The documents are records about a resident's care
      // and home — medical, housing, financial, legal — and the person who grants a
      // zorgcoördinator `documents.read.medical` is the person who runs the home, not the
      // person who runs the portal. The three PROTECTED admin pages (grants, exclusions,
      // archive) sit here too rather than under 'admin': the partition rule forces one
      // home for the whole `documents` group, and an area is what an admin THINKS IN.
      // PROTECTED is unaffected by the area — it is enforced by PROTECTED_PERMISSIONS.
      "documents"
    ]
  },
  {
    id: "technical",
    label: "Devices & technical",
    description: "The installed estate and the systems behind it: devices, firmware, telephony, Qbus, EnOcean, wayfinding.",
    groups: [
      "devices",
      "device_types",
      "device_linking",
      "firmware",
      "enocean",
      "qbus",
      "telephony",
      "baseip",
      "eink",
      "indoor",
      "services",
      "automations"
    ]
  },
  {
    id: "commerce",
    label: "Billing & till",
    description: "The two paid modules. A permission here still does nothing unless the organisation has bought the module \u2014 they are independent gates and both must pass.",
    groups: ["invoicing", "pos", "catalog"]
  },
  {
    id: "admin",
    label: "Administration",
    description: "Running the portal itself: accounts and their rights, company settings, monitoring, and the knowledge base.",
    groups: [
      "users",
      "system",
      "settings",
      "notifications",
      "integrations",
      "monitoring",
      "knowledge_base",
      "ideas",
      "dashboard",
      "auth",
      // The audit trail is a governance surface over the portal itself, so it belongs
      // with the other administration pages rather than with the data it reports on.
      "audit"
    ]
  }
];
var AREA_BY_GROUP = PERMISSION_AREAS.reduce(
  (acc, area) => {
    for (const g of area.groups) acc[g] = area.id;
    return acc;
  },
  {}
);
function areaForPage(pageId) {
  const entry = PAGE_REGISTRY.find((p) => p.id === pageId);
  if (!entry) return null;
  return AREA_BY_GROUP[entry.group] ?? null;
}
function keysForArea(areaId) {
  const area = PERMISSION_AREAS.find((a) => a.id === areaId);
  if (!area) return [];
  const keys = /* @__PURE__ */ new Set();
  for (const page of PAGE_REGISTRY) {
    if (!area.groups.includes(page.group)) continue;
    for (const req of page.requiredPermissions) keys.add(req.key);
  }
  return [...keys].sort();
}
function unmappedGroups() {
  const seen = /* @__PURE__ */ new Set();
  for (const p of PAGE_REGISTRY) {
    if (p.isPublic) continue;
    if (!AREA_BY_GROUP[p.group]) seen.add(p.group);
  }
  return [...seen].sort();
}

// src/constants/documentClassification.ts
var DOCUMENT_LEVELS = ["openbaar", "intern", "vertrouwelijk", "strikt"];
var DOCUMENT_LEVEL_ORDER = Object.freeze(
  Object.fromEntries(DOCUMENT_LEVELS.map((lvl, i) => [lvl, i]))
);
var BASELINE_TOPICS = ["medical", "financial", "legal", "technical", "housing", "hr"];
var TOPIC_KEY_PATTERN = /^[a-z0-9-]{1,64}$/;
function isDocumentLevel(value) {
  return typeof value === "string" && DOCUMENT_LEVELS.includes(value);
}
function compareLevels(a, b) {
  return DOCUMENT_LEVEL_ORDER[a] - DOCUMENT_LEVEL_ORDER[b];
}
function maxLevel(levels) {
  let best = null;
  for (const lvl of levels) {
    if (!isDocumentLevel(lvl)) continue;
    if (best === null || compareLevels(lvl, best) > 0) best = lvl;
  }
  return best;
}
function isBaselineTopic(value) {
  return typeof value === "string" && BASELINE_TOPICS.includes(value);
}
function topicPermissionKey(topic) {
  if (!TOPIC_KEY_PATTERN.test(topic)) {
    throw new Error(`Invalid document topic key: ${JSON.stringify(topic)}`);
  }
  return `documents.read.${topic}`;
}
var DEFAULT_RETENTION_YEARS = 10;
var MANUAL_GRANT_MAX_MONTHS = 6;
var EMERGENCY_GRANT_HOURS = 72;
var EXCLUSION_MAX_MONTHS = 12;
var RECERT_MONTHS = 6;
var RECERT_GRACE_DAYS = 30;
var EXCLUSION_REASON_MIN_CHARS = 20;
var PURGE_TYPED_COUNT_THRESHOLD = 100;

// src/constants/agendaEntryTypes.ts
var AGENDA_ENTRY_TYPES = [
  // ── The four the owner named ──────────────────────────────────────────────
  { key: "maintenance", icon: "\u{1F527}", color: "#0E7490", residentVisible: true, group: "facility" },
  { key: "technician", icon: "\u{1F6E0}\uFE0F", color: "#0369A1", residentVisible: true, group: "facility" },
  { key: "leave", icon: "\u{1F334}", color: "#65A30D", residentVisible: false, group: "internal" },
  { key: "training", icon: "\u{1F393}", color: "#7C3AED", residentVisible: false, group: "internal" },
  // ── Practical, and residents notice all of them ───────────────────────────
  { key: "delivery", icon: "\u{1F4E6}", color: "#B45309", residentVisible: true, group: "site" },
  { key: "fire_drill", icon: "\u{1F6A8}", color: "#DC2626", residentVisible: true, group: "site" },
  { key: "cleaning", icon: "\u{1F9F9}", color: "#0891B2", residentVisible: true, group: "site" },
  { key: "closure", icon: "\u{1F512}", color: "#6B7280", residentVisible: true, group: "site" },
  // ── Internal organisation ─────────────────────────────────────────────────
  { key: "meeting", icon: "\u{1F465}", color: "#4F46E5", residentVisible: false, group: "internal" },
  { key: "audit", icon: "\u{1F4CB}", color: "#9333EA", residentVisible: false, group: "internal" },
  // A move is disruptive and visible in the corridor — the one internal-group type
  // residents are told about by default.
  { key: "moving", icon: "\u{1F69A}", color: "#A16207", residentVisible: true, group: "internal" },
  // ── Tied to ONE resident: the hairdresser case from the spec ──────────────
  { key: "doctor", icon: "\u{1FA7A}", color: "#BE123C", residentVisible: true, group: "resident" },
  { key: "hairdresser", icon: "\u2702\uFE0F", color: "#DB2777", residentVisible: true, group: "resident" },
  { key: "pedicure", icon: "\u{1F485}", color: "#E11D48", residentVisible: true, group: "resident" },
  // The escape hatch. Without one, staff type the real kind into the title and the
  // filter stops meaning anything.
  { key: "other", icon: "\u{1F4CC}", color: "#475569", residentVisible: true, group: "site" }
];
var AGENDA_ENTRY_TYPE_KEYS = AGENDA_ENTRY_TYPES.map((t) => t.key);
var BY_KEY = new Map(AGENDA_ENTRY_TYPES.map((t) => [t.key, t]));
function agendaEntryType(key) {
  return key && BY_KEY.get(key) || BY_KEY.get("other");
}
function isAgendaEntryType(value) {
  return typeof value === "string" && BY_KEY.has(value);
}
function defaultVisibilityForType(key) {
  return agendaEntryType(key).residentVisible ? "shared" : "staff_only";
}

// src/constants/inspections.ts
var INSPECTION_ITEM_STATES = [
  "nieuw",
  "goed",
  "gebruikssporen",
  "beschadigd",
  "ontbreekt"
];
var INSPECTION_ITEM_STATE_LABELS = {
  nieuw: "Nieuw",
  goed: "Goed",
  gebruikssporen: "Gebruikssporen",
  beschadigd: "Beschadigd",
  ontbreekt: "Ontbreekt"
};
var INSPECTION_DEFECT_STATES = ["beschadigd", "ontbreekt"];
var INSPECTION_CHANGE_KINDS = ["normale_slijtage", "schade"];
var INSPECTION_CHANGE_KIND_LABELS = {
  normale_slijtage: "Normale slijtage",
  schade: "Schade"
};
var INSPECTION_MOMENTS = ["intake", "departure", "interim", "correction"];
var INSPECTION_MOMENT_LABELS = {
  intake: "Intrede",
  departure: "Uittrede",
  interim: "Tussentijds",
  correction: "Correctie"
};
var INSPECTION_STATUSES = [
  "draft",
  "in_review",
  "published",
  "published_without_confirmation",
  "cancelled"
];
var INSPECTION_STATUS_LABELS = {
  draft: "Ontwerp",
  in_review: "Ter bevestiging",
  published: "Gepubliceerd",
  published_without_confirmation: "Gepubliceerd zonder bevestiging",
  cancelled: "Geannuleerd"
};
var INSPECTION_FROZEN_STATUSES = [
  "published",
  "published_without_confirmation"
];
var INSPECTION_SIGNATURE_ROUTES = ["device", "link", "paper"];
var INSPECTION_SIGNATURE_ROUTE_LABELS = {
  device: "Getekend op het toestel",
  link: "Online bevestigd",
  paper: "Handtekeningen op papier (scan)"
};
var INSPECTION_SIGNATURE_ROLES = [
  "staff",
  "resident",
  "representative",
  "witness"
];
var INSPECTION_COUNTERPARTY_ROLES = [
  "resident",
  "representative"
];
var METER_UTILITIES = [
  "water",
  "gas",
  "electricity",
  "electricity_night",
  "heat",
  "hot_water"
];
var METER_UTILITY_LABELS = {
  water: "Water",
  gas: "Gas",
  electricity: "Elektriciteit",
  electricity_night: "Elektriciteit (nacht)",
  heat: "Warmte",
  hot_water: "Warm water"
};
var METER_DEFAULT_UNITS = {
  water: "m3",
  gas: "m3",
  electricity: "kWh",
  electricity_night: "kWh",
  heat: "GJ",
  hot_water: "m3"
};
var MAX_CURRENT_FLOOR_PLANS_PER_UNIT = 2;
var FLOOR_PLAN_MIN_LONG_EDGE_PX = 1200;
var FLOOR_PLAN_WARN_LONG_EDGE_PX = 2e3;
var FLOOR_PLAN_ACCEPTED_MIME = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif"
];
var INSPECTION_PHOTO_MAX_LONG_EDGE_PX = 1920;
var INSPECTION_PHOTO_JPEG_QUALITY = 0.8;

export { AGENDA_ENTRY_TYPES, AGENDA_ENTRY_TYPE_KEYS, API_DEFAULTS, API_ENDPOINTS, APP_NAME, APP_VERSION, AREA_BY_GROUP, AUTH_KEYS, BASELINE_TOPICS, BILLABLE_SOURCE_TYPES, CACHE_CONFIG, CAMERA_REQUEST_STATUS, DATE_FORMATS, DEFAULT_RETENTION_YEARS, DEVICE_STATUS, DEVICE_TYPES, DOCUMENT_LEVELS, DOCUMENT_LEVEL_ORDER, DOOR_RELEASE_TABLET_BUDGET_MS, EMERGENCY_GRANT_HOURS, ERROR_CODES, ERROR_MESSAGES, ESTIMATE_APPROVAL_DEFAULT_DAYS, ESTIMATE_APPROVAL_MAX_DAYS, ESTIMATE_APPROVAL_MIN_DAYS, ESTIMATE_STATUS, EXCLUSION_MAX_MONTHS, EXCLUSION_REASON_MIN_CHARS, EXTENSION_RANGES, FILE_UPLOAD, FLOOR_PLAN_ACCEPTED_MIME, FLOOR_PLAN_MIN_LONG_EDGE_PX, FLOOR_PLAN_WARN_LONG_EDGE_PX, FULL_ACCESS_ROLES, HTTP_METHODS, INSPECTION_CHANGE_KINDS, INSPECTION_CHANGE_KIND_LABELS, INSPECTION_COUNTERPARTY_ROLES, INSPECTION_DEFECT_STATES, INSPECTION_FROZEN_STATUSES, INSPECTION_ITEM_STATES, INSPECTION_ITEM_STATE_LABELS, INSPECTION_MOMENTS, INSPECTION_MOMENT_LABELS, INSPECTION_PHOTO_JPEG_QUALITY, INSPECTION_PHOTO_MAX_LONG_EDGE_PX, INSPECTION_SIGNATURE_ROLES, INSPECTION_SIGNATURE_ROUTES, INSPECTION_SIGNATURE_ROUTE_LABELS, INSPECTION_STATUSES, INSPECTION_STATUS_LABELS, INVOICING_SUBDIVISIONS, IN_STOCK_STATUS, LOCATION_TYPES, MANUAL_GRANT_MAX_MONTHS, MAP_DEFAULTS, MAX_CURRENT_FLOOR_PLANS_PER_UNIT, METER_DEFAULT_UNITS, METER_UTILITIES, METER_UTILITY_LABELS, METHODS_WITH_BODY, NOTIFICATION_TYPES, PAGE_CUSTOM_VERBS, PAGE_REGISTRY, PAGE_STANDARD_VERBS, PAGE_VERBS, PAGINATION, PERMISSION_AREAS, PERMISSION_DESCRIPTIONS, PERMISSION_GROUPS, PERMISSION_INCOMPATIBLE_PAIRS, PERMISSION_KEYS, PORTAL_ROLES, POS_CONSUMPTION_MODES, POS_IDENTIFICATION_MODES, POS_PRODUCT_ICONS, POS_SETTLEMENTS, POS_STOCK_MODES, POS_TAX_CATEGORIES, POS_TILE_SIZES, PROTECTED_PERMISSIONS, PURGE_TYPED_COUNT_THRESHOLD, RECERT_GRACE_DAYS, RECERT_MONTHS, REGEX, RESIDENT_TAX_PROFILE, ROLE_DEFAULT_PERMISSIONS, SIP_DEFAULTS, SITE_CUSTOM_FIELDS, SUCCESS_MESSAGES, SUPPORT_CONTACT, THEME_COLORS, TOPIC_KEY_PATTERN, USER_ROLES, USER_ROLE_DESCRIPTIONS, USER_ROLE_LABELS, WS_CONFIG, agendaEntryType, areaForPage, compareLevels, defaultVisibilityForType, getCameraRequestStatusMeta, getEstimateStatusMeta, getInStockMeta, getPageById, getPageByRoute, getPagesByGroup, getUngatedPages, isAgendaEntryType, isBaselineTopic, isDocumentLevel, isEmergencyExtension, isEntranceDoorExtension, isFullAccessRole, isHttpMethod, isProtectedPermission, isUngatedPage, isVideophoneExtension, keysForArea, maxLevel, methodAllowsBody, normalizeWebhookMethod, topicPermissionKey, unmappedGroups, visibilityPermissionForRoute };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map